// src/pages/BattleAnalyzer.js
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Tesseract from 'tesseract.js';

import { saveRaidCloud, getMyRaids, deleteRaidCloud, deleteUserAnalysesByKey } from '../firebaseClient';
import { useAuth } from '../contexts/AuthContext';

import StepProgressBar from '../components/StepProgressBar';
import ControlsBar from '../components/ControlsBar';
import ScreenCapture from '../components/ScreenCapture';
import ProfileInfoCard from '../components/ProfileInfoCard';
import TraitsList from '../components/TraitsList';
import SavedRaidsPanel from '../components/SavedRaidsPanel';
import BattleMetaInfo from '../components/BattleMetaInfo';

import { ROI, ROI_SECOND_PASS_16, ROI_SECOND_PASS_18 } from '../constants/battle/roi';
import { RAID_NAMES } from '../constants/battle/raids';
import { JOB_CONFIG, KNOWN_JOBS, ROLE_COMMON_TRAITS } from '../constants/battle/jobs';


/* =========================================================
 * 기본 상수/ROI 프리셋
 * ======================================================= */
const MYCHAR_ROI = ROI?.myCharCheck || { x: 1211, y: 87, w: 50, h: 28 };
const MYCHAR_ROI2 = ROI?.myCharCheck2 || { x: 1269, y: 87, w: 50, h: 28 };

const ROI_PRESETS = {
  idle: { x: 859, y: 1, w: 1060, h: 690 },
  profile: { x: 859, y: 1, w: 1060, h: 690 },
  battleMain: { x: 447, y: 1, w: 1472, h: 902 },
  battleMain2: { x: 447, y: 1, w: 1472, h: 902 },
  done: { x: 447, y: 1, w: 1472, h: 902 },
};

// ==== Party Lite 배지 캐시 동기화용 유틸 ====
const PARTY_SAVED_KEYS_PREFIX = 'partyCloudSavedKeys:';
const savedKeyStorageKey = (uid) => `${PARTY_SAVED_KEYS_PREFIX}${uid || 'guest'}`;

// Lite와 동일한 키 규칙(raidName/timestamp/battleTime)
function makeCompositeKeyForPartyBadge(r) {
  const raidName   = r?.raidName   ?? r?.payload?.raidName   ?? '';
  const timestamp  = r?.timestamp  ?? r?.payload?.timestamp  ?? '';
  const battleTime = r?.battleTime ?? r?.payload?.battleTime ?? '';
  return `${raidName}__${timestamp}__${battleTime}`;
}


// 선택된 클라우드 항목(혹은 로컬+클라우드 병합 목록) 원본 가져오기
function getSelectedRecords(selectedIds, savedRaids) {
  const idSet = new Set(selectedIds);
  return (savedRaids || []).filter(r => idSet.has(r.id));
}


/* =========================================================
 * 문자열 유사도/매칭 유틸
 * ======================================================= */
function levenshtein(a, b) {
  const dp = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) dp[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[a.length][b.length];
}
function similarity(a, b) {
  if (!a || !b) return 0;
  if (!a.length && !b.length) return 1;
  return 1 - levenshtein(a, b) / Math.max(a.length, b.length);
}
function uniqueMatchWithScores(ocrTexts, candidates) {
  const pairs = [];
  for (let i = 0; i < ocrTexts.length; i++) {
    const src = (ocrTexts[i] || '').trim();
    for (let j = 0; j < candidates.length; j++) {
      const tgt = candidates[j];
      const score = similarity(src, tgt);
      pairs.push({ i, j, score });
    }
  }
  pairs.sort((a, b) => b.score - a.score);
  const usedRows = new Set();
  const usedCols = new Set();
  const results = Array(ocrTexts.length).fill(null);
  for (const { i, j, score } of pairs) {
    if (usedRows.has(i) || usedCols.has(j)) continue;
    results[i] = { best: candidates[j], score, index: j };
    usedRows.add(i);
    usedCols.add(j);
  }
  return results;
}
function findBestMatchWithScore(text, candidates) {
  let best = candidates[0] || text;
  let bestScore = 0;
  candidates.forEach(c => {
    const score = similarity(text, c);
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  });
  return { best, score: bestScore };
}
function isDamageForcedByKeyword(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (!n) return false;
  return n.includes('피해') && n.includes('량');
}


/* =========================================================
 * 숫자/단위 정규화 유틸 (OCR 수치 처리)
 * ======================================================= */
function extractNumberLoose(str) {
  const s = String(str || '');
  let m = s.match(/[0-9][0-9,]*\.?[0-9]*/);
  if (m) return m[0];
  const fixed = fixDigitLookalikes(s);
  m = fixed.match(/[0-9][0-9,]*\.?[0-9]*/);
  return m ? m[0] : '';
}
function normalizeNumericString(s) {
  const str = (s || '').replace(/\s/g, '');
  if (!str.includes('.') && (str.match(/,/g) || []).length === 1) {
    const m = str.match(/^(\d+),(\d{1,2})(?!\d)/);
    if (m) return `${m[1]}.${m[2]}`;
  }
  const dotCount = (str.match(/\./g) || []).length;
  const commaCount = (str.match(/,/g) || []).length;
  if (dotCount > 1 && commaCount === 0) {
    const last = str.lastIndexOf('.');
    const intPart = str.slice(0, last).replace(/\./g, '');
    const fracPart = str.slice(last + 1).replace(/\./g, '');
    return intPart + (fracPart ? '.' + fracPart : '');
  }
  if (commaCount > 1 && dotCount === 0) return str.replace(/,/g, '');
  if (dotCount >= 1 && commaCount >= 1) {
    const lastDot = str.lastIndexOf('.');
    const lastComma = str.lastIndexOf(',');
    const decIdx = Math.max(lastDot, lastComma);
    const intPart = str.slice(0, decIdx).replace(/[.,]/g, '');
    const fracPart = str.slice(decIdx + 1).replace(/[.,]/g, '');
    return intPart + (fracPart ? '.' + fracPart : '');
  }
  if (commaCount === 1 && dotCount === 0) return str.replace(/,/g, '');
  return str;
}
function normalizePercentNumber(numStr) {
  const s = String(numStr || '').replace(/\s/g, '');
  if (!s) return s;
  if (s.includes('.')) return s;
  if (s.length > 2) return s.slice(0, -2) + '.' + s.slice(-2);
  return s;
}
function normalizeFixed4_2(str) {
  const digits = String(str || '').replace(/[^0-9]/g, '');
  if (digits.length < 3) return str || '';
  const d = digits.slice(0, 6);
  const intLen = Math.min(Math.max(d.length - 2, 1), 4);
  const intPart = d.slice(0, intLen);
  const fracPart = d.slice(intLen, intLen + 2).padEnd(2, '0');
  return `${intPart}.${fracPart}`;
}
function parseRawValueStrict(str) {
  const src = String(str || '');
  const hadPercent = /[%％]/.test(src);
  let nStr = extractNumberLoose(src);
  if (hadPercent) nStr = normalizePercentNumber(nStr);
  nStr = normalizeNumericString(nStr);
  const n = parseFloat(nStr);
  return Number.isFinite(n) ? n : 0;
}
function formatValueWithUnit(valueStr, traitName) {
  const raw = fixDigitLookalikes(String(valueStr || '').trim());
  const numericCore = extractNumberLoose(raw);
  const numericNorm = normalizeNumericString(numericCore);
  const mm = numericNorm.match(NUM_ONLY_RE);
  if (!mm) {
    if (isPercentTraitName(traitName)) return '0%';
    if (isCountTraitName(traitName)) return '0회';
    return '0';
  }
  const intPart = mm[1];
  const fracFull = mm[2] || '';
  const hadFrac = fracFull.length > 0;
  const overflow = fracFull.slice(2);
  const shown = toTwoDecimalsTruncate(`${intPart}${hadFrac ? '.' + fracFull : ''}`);
  const numericShown = parseFloat(shown.replace(/,/g, '')) || 0;
  const isZero = numericShown === 0;
  const origUnit = (raw.match(/([만억조회%])$/) || [])[1] || '';
  if (isZero) {
    if (isPercentTraitName(traitName)) return '0%';
    if (isCountTraitName(traitName)) return '0회';
    return '0';
  }
  if (isDamageForcedByKeyword(traitName) || isDamageLikeName(traitName)) {
    const { cands, fallback } = damageUnitCandidates(traitName);
    const inferred = inferUnitGeneric(overflow, cands, fallback, 0.60);
    const origUnitDamage = /[만억조]/.test(origUnit) ? origUnit : '';
    let finalUnit = origUnitDamage || inferred || fallback || '억';
    if (finalUnit === '%') {
      finalUnit = fallback || '억';
    }
    const isReceived = /받은피해량/.test(String(traitName).replace(/\s+/g, ''));
    const looksBareUpTo4Digits =
      /^([0-9]{1,4})(?:\.0{1,2})?$/.test(numericNorm) && !origUnit;
    if (isReceived && looksBareUpTo4Digits) {
      return `${shown}`;
    }
    return `${shown}${finalUnit}`;
  }
  if (isCountTraitName(traitName)) return `${intPart}회`;
  if (isMuleoghName(traitName)) {
    return hadFrac ? `${shown}만` : shown;
  }
  if (isPercentTraitName(traitName)) {
    const inferred = inferUnitGeneric(overflow, ['%'], '%', 0.6);
    const unit = origUnit || inferred || '%';
    return `${shown}${unit}`;
  }
  return shown;
}


/* =========================================================
 * 피해/단위 후검증 유틸 (DPS vs 총합 등)
 * ======================================================= */
function nameKey(s) { return String(s || '').replace(/\s+/g, '').toLowerCase(); }
function isTotalDamageName(name) {
  const n = nameKey(name);
  return n.includes('피해') && n.includes('량') && !n.includes('초당') && !n.includes('1분') && !n.includes('받은');
}
function isDpsDamageName(name) {
  const n = nameKey(name);
  return n.includes('초당') && n.includes('피해') && n.includes('량') && !n.includes('1분');
}
function isMinTotalDamageName(name) {
  const n = nameKey(name);
  return n.includes('1분') && n.includes('피해') && n.includes('량') && !n.includes('초당');
}
function isMinDpsDamageName(name) {
  const n = nameKey(name);
  return n.includes('1분') && (n.includes('초당') || n.includes('dps')) && n.includes('피해') && n.includes('량');
}
function nextLowerUnit(u) {
  if (u === '조') return '억';
  if (u === '억') return '만';
  return '만';
}
function setUnitKeepNumber(valueStr, newUnit) {
  const s = String(valueStr || '').trim();
  const m = s.match(/^([0-9][0-9,]*)(?:\.([0-9]+))?/);
  if (!m) return s;
  const numStr = toTwoDecimalsTruncate((m[1] || '') + (m[2] ? '.' + m[2] : ''));
  return `${numStr}${newUnit}`;
}
function parseNumOnly(valueStr) {
  const m = String(valueStr || '').trim().match(/^([0-9][0-9,]*)(?:\.([0-9]+))?/);
  if (!m) return null;
  const numStr = toTwoDecimalsTruncate((m[1] || '') + (m[2] ? '.' + m[2] : ''));
  return parseFloat(numStr.replace(/,/g, ''));
}
function detectUnit(valueStr) {
  return (String(valueStr || '').trim().match(/([만억조회%])$/) || [])[1] || '';
}
function enforceDamageRateUnits(mainTraits, addTraits) {
  const all = [...(mainTraits || []), ...(addTraits || [])];
  const total = all.find(t => isTotalDamageName(t.trait));
  const dps = all.find(t => isDpsDamageName(t.trait));
  const minTot = all.find(t => isMinTotalDamageName(t.trait));
  const minDps = all.find(t => isMinDpsDamageName(t.trait));
  const defaultDamageUnit = '억';
  if (total && dps) {
    const totalNum = parseNumOnly(total.value);
    const dpsNum = parseNumOnly(dps.value);
    const totalUnit = detectUnit(total.value) || defaultDamageUnit;
    if (Number.isFinite(totalNum) && Number.isFinite(dpsNum)) {
      if (dpsNum > totalNum) {
        dps.value = setUnitKeepNumber(dps.value, nextLowerUnit(totalUnit));
      }
    }
  }
  if (minTot && minDps) {
    const totalNum = parseNumOnly(minTot.value);
    const dpsNum = parseNumOnly(minDps.value);
    const totalUnit = detectUnit(minTot.value) || defaultDamageUnit;
    if (Number.isFinite(totalNum) && Number.isFinite(dpsNum)) {
      if (dpsNum > totalNum) {
        minDps.value = setUnitKeepNumber(minDps.value, nextLowerUnit(totalUnit));
      }
    }
  }
  return { main: mainTraits, add: addTraits };
}


/* =========================================================
 * 비교/표시 포맷 유틸 (헤더/시간/평균)
 * ======================================================= */
function trim2(v) {
  let s = Number(v ?? 0).toFixed(2);
  s = s.replace(/\.00$/, '');
  s = s.replace(/(\.\d)0$/, '$1');
  return s;
}
function formatCompareNumber(v1, v2, suffix = '') {
  const a = parseFloat(v1) || 0;
  const b = parseFloat(v2) || 0;
  const sign = (b - a) >= 0 ? '↑' : '↓';
  const diff = Math.abs(b - a);
  return `${trim2(a)}${suffix} → ${trim2(b)}${suffix} (${sign}${trim2(diff)}${suffix})`;
}
function formatMMSS(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function formatCompareTime(t1, t2) {
  const s1 = parseTime(t1);
  const s2 = parseTime(t2);
  const sign = (s2 - s1) >= 0 ? '↑' : '↓';
  const delta = Math.abs(s2 - s1);
  return `${formatMMSS(s1)} > ${formatMMSS(s2)} (${sign}${formatMMSS(delta)})`;
}
function averageNumbers(nums) {
  const arr = nums.map(n => parseFloat(n)).filter(Number.isFinite);
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}
function averageTimesMMSS(times) {
  const secs = times.map(parseTime).filter(n => Number.isFinite(n));
  if (!secs.length) return '00:00';
  const avg = Math.round(secs.reduce((a, b) => a + b, 0) / secs.length);
  return formatMMSS(avg);
}


/* =========================================================
 * 레이드명 보정/스코어 유틸 (한글·기호 변형 대응)
 * ======================================================= */
const FULLWIDTH_DIGITS_RE = /[０-９]/g;
const full2halfDigits = s =>
  String(s || '').replace(FULLWIDTH_DIGITS_RE, ch =>
    String.fromCharCode(ch.charCodeAt(0) - 0xFF10 + 0x30)
  );
function normalizeRaidNameForMatch(s) {
  let t = full2halfDigits(String(s || '')).trim();
  t = t.replace(/[：﹕꞉··⋅・]/g, ':');
  t = t.replace(/^(\d+)\s*[%‰]\s*:/, '$1막 :');
  // 관문 앞 1 오인식 보정(한글 ㅣ, 로마 Ⅰ, 각종 세로바 포함)
  t = t.replace(/([lI|!ｌＩ])(?=\s*관문)/g, '1');
  // 관문 앞 1 오인식 보정(한글 ㅣ, 로마 Ⅰ, 각종 세로바 포함) + 사이에 문장부호 허용
  // 예: "ㅣ。관문", "Ⅰ 관문", "|, 관문" 등
  t = t.replace(
    /([lI|!ｌＩㅣⅠ❘︱｜│])[.\-_,·⋅・·:;，、。／/\\|\s]*(?=관문)/g,
    '1'
  );
  // ‘관문’만 있고 숫자 없으면 1관문으로 보정
  if (/관문/.test(t) && !/\d+\s*관문/.test(t)) {
    t = t.replace(/관문/g, '1관문');
  }
  t = t.replace(/\s+/g, ' ').trim();
  return t;
}
function extractStageAndGate(s) {
  const t = normalizeRaidNameForMatch(s).replace(/\s+/g, '');
  let stage = null, gate = null;
  const mStage = t.match(/^(\d+)막/);
  if (mStage) stage = parseInt(mStage[1], 10);
  // 숫자와 '관문' 사이에 문장부호(.,、，。 / 등) 허용
  const mGate = t.match(/(\d+)[.\-_,·⋅・·:;，、。／/\\|\s]*관문/);
  if (mGate) gate = parseInt(mGate[1], 10);
  return { stage, gate };
}


/* =========================================================
 * 숫자 보정/이름 분류/단위 추론 유틸
 * ======================================================= */
function fixDigitLookalikes(s) {
  if (!s) return s;
  let t = String(s);
  t = t.replace(/\bpi\b/gi, '2');
  t = t.replace(/[Oo]/g, '0');
  t = t.replace(/[Il]/g, '1');   // 영문 I,l → 1
  t = t.replace(/ㅣ/g, '1');     // 한글 'ㅣ' → 1
  t = t.replace(/[Zz]/g, '2');
  t = t.replace(/[Ss]/g, '5');
  t = t.replace(/[gqGQ]/g, '9');
  return t;
}
function toTwoDecimalsTruncate(numStr) {
  const s = String(numStr || '');
  const m = s.match(/^([0-9][0-9,]*)(?:\.([0-9]+))?$/);
  if (!m) return s;
  const intPart = m[1];
  const frac = (m[2] || '');
  const frac2 = frac.slice(0, 2).padEnd(2, '0');
  return frac ? `${intPart}.${frac2}` : intPart;
}
function isCountTraitName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return n.includes('횟수');
}
function isMuleoghName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return n.includes('무력화');
}
function isPercentTraitName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (n.includes('받은피해량')) return false;
  return /[율률]|가동률|유효율|적중률/.test(n);
}
function isDamageLikeName(name = '') {
  const n = String(name).replace(/\s+/g, '');
  return /(피해.*량|초당피해량|1분피해량|받은피해량)/.test(n);
}
function damageUnitCandidates(name = '') {
  const n = String(name).replace(/\s+/g, '');
  if (n.includes('받은피해량')) {
    return { cands: ['만', '억', '조'], fallback: '만' };
  }
  return { cands: ['억', '만', '조'], fallback: '억' };
}
function inferUnitGeneric(overflow, candidates, fallback, acceptRatio = 0.6) {
  const src = String(overflow || '').trim();
  if (!src) return null;
  let best = null;
  let bestDist = Infinity;
  for (const unit of candidates) {
    const d = levenshtein(src, unit);
    if (d < bestDist) { bestDist = d; best = unit; }
  }
  const denom = Math.max(src.length, (best || '').length, 1);
  const ratio = bestDist / denom;
  if (ratio <= acceptRatio) return best;
  return fallback ?? null;
}
const NUM_ONLY_RE = /^([0-9][0-9,]*)(?:\.([0-9]+))?$/;


/* =========================================================
 * 공통 헬퍼 (문자/시간/타임스탬프/병합)
 * ======================================================= */
function toCloudDoc(r) {
  const { _local, _cloud, source, ...clean } = r;
  return clean;
}
function mergeLocalAndCloud(local, cloud) {
  const localMap = new Map(local.map(r => [r.id, { ...r, _local: true, _cloud: !!r._cloud, source: 'local' }]));
  const cloudMap = new Map(cloud.map(r => [r.id, { ...r, _local: false, _cloud: true, source: 'cloud' }]));
  const ids = new Set([...localMap.keys(), ...cloudMap.keys()]);
  const merged = [];
  for (const id of ids) {
    const l = localMap.get(id);
    const c = cloudMap.get(id);
    if (l && c) {
      merged.push({
        ...c,
        ...l,
        _local: true,
        _cloud: true,
        source: 'cloud',
      });
    } else if (c) {
      merged.push({ ...c, _local: false, _cloud: true, source: 'cloud' });
    } else if (l) {
      merged.push({ ...l, _local: true, _cloud: false, source: 'local' });
    }
  }
  return merged.sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
}
function normalizeTimestamp(raw) {
  const digits = (raw || '').replace(/[^0-9]/g, ' ').trim();
  const parts = digits.split(/\s+/).map(n => parseInt(n, 10));
  if (parts.length >= 6) {
    const [y, mo, da, hh, mi, ss] = parts;
    const z2 = v => String(v).padStart(2, '0');
    return `${y}.${z2(mo)}.${z2(da)} ${z2(hh)}:${z2(mi)}:${z2(ss)}`;
  }
  return raw || '';
}
function parseTime(str) {
  const parts = (str || '').split(':').map(p => parseInt(p, 10));
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return isNaN(parts[0]) ? 0 : parts[0];
}
function formatDiffTime(sec) {
  const sign = sec >= 0 ? '+ ' : '- ';
  const abs = Math.abs(sec);
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
function compact(s) { return (s || '').replace(/\s+/g, ''); }


/* =========================================================
 * 직업/각인 메타 조회 (ORDER/개수 등)
 * ======================================================= */
function getEngravingMeta(jobName, engravingName) {
  if (!jobName || !engravingName) return null;
  const job = JOB_CONFIG[jobName];
  if (!job || !Array.isArray(job.engravings)) return null;
  return job.engravings.find(e => e?.name === engravingName) || null;
}
function getJobConfig(job, engravingName) {
  const base = JOB_CONFIG._base || {};
  const jobCfg = JOB_CONFIG[job] || {};
  const eng = getEngravingMeta(job, engravingName);
  const role = eng?.role || null;
  const roleTraits = role ? (ROLE_COMMON_TRAITS[role] || []) : [];
  const traitNames = Array.from(
    new Set([
      ...(base.commonTraits || []),
      ...roleTraits,
      ...((jobCfg.addTraits) || []),
      ...((eng?.addTraits) || []),
    ])
  );
  const mainCount =
    Number.isFinite(jobCfg.mainCount) ? jobCfg.mainCount :
      Number.isFinite(base.mainCountDefault) ? base.mainCountDefault : 4;
  const addCount =
    Number.isFinite(eng?.addCount) ? eng.addCount :
      Number.isFinite(jobCfg.addCount) ? jobCfg.addCount :
        Number.isFinite(base.addCountDefault) ? base.addCountDefault : 13;
  const engravings = (jobCfg.engravings || []).map(e => e?.name).filter(Boolean);
  return { traitNames, mainCount, addCount, engravings, role };
}


/* =========================================================
 * 평균/정렬/집계 유틸
 * ======================================================= */
function parseRawValue(str, traitName) {
  const n = parseRawValueStrict(str, traitName);
  if (!Number.isFinite(n)) return 0;
  const unit = (String(str || '').trim().match(/([만억조%회])$/) || [])[1] || '';
  const needsUnitScaling =
    isDamageLikeName(traitName) ||
    isTotalDamageName?.(traitName) ||
    isDpsDamageName?.(traitName) ||
    isMinTotalDamageName?.(traitName) ||
    isMinDpsDamageName?.(traitName) ||
    isMuleoghName(traitName);
  if (!needsUnitScaling) return n;
  let mul = 1;
  if (unit === '만') mul = 1e4;
  else if (unit === '억') mul = 1e8;
  else if (unit === '조') mul = 1e12;
  return n * mul;
}
function traitsToMap(main = [], add = []) {
  const map = new Map();
  const put = (t) => {
    if (!t || !t.trait) return;
    const unit = (t.value.match(/[^0-9.,]+$/) || [''])[0];
    map.set(t.trait, {
      trait: t.trait,
      value: t.value,
      unit,
      raw: parseRawValue(t.value, t.trait),
    });
  };
  main.forEach(put);
  add.forEach(put);
  return map;
}
function sortByKnownOrder(items, knownOrder = []) {
  const idx = new Map(knownOrder.map((n, i) => [n, i]));
  return [...items].sort((a, b) => {
    const ia = idx.has(a.trait) ? idx.get(a.trait) : Number.MAX_SAFE_INTEGER;
    const ib = idx.has(b.trait) ? idx.get(b.trait) : Number.MAX_SAFE_INTEGER;
    if (ia !== ib) return ia - ib;
    return a.trait.localeCompare(b.trait);
  });
}
function normalizeTraitName(name) {
  if (!name) return '';
  const n = name.replace(/\s+/g, '').toLowerCase();
  if (n.includes('저스트가드')) return '저스트 가드 성공 횟수';
  if (n === '1분피해량' || n === '1분dps') return '1분 피해량';
  return name;
}
function groupTraitsByName(records) {
  const bucket = {};
  records.forEach(r => {
    const all = []
      .concat(r.mainTraits || [])
      .concat(r.additionalTraits || []);
    all.forEach(t => {
      const name = normalizeTraitName((t.trait || '').trim());
      if (!name) return;
      const val = String(t.value || '');
      const raw = parseRawValue(val, name);
      const unit = (val.match(/[^0-9.,]+$/) || [''])[0];
      if (!bucket[name]) bucket[name] = [];
      bucket[name].push({ raw, unit });
    });
  });
  return bucket;
}
function formatByUnitForDisplay(raw, traitName) {
  if (!Number.isFinite(raw)) return '';
  const isCount = isCountTraitName(traitName);
  const isRate = isPercentTraitName(traitName);
  if (isCount) {
    let s = raw.toFixed(2);
    s = s.replace(/\.00$/, '');
    s = s.replace(/(\.\d)0$/, '$1');
    return s + '회';
  }
  if (isRate) {
    let s = raw.toFixed(2) + '%';
    s = s.replace(/\.00%$/, '%');
    return s;
  }
  const isMagnitudeBased =
    isMuleoghName(traitName) ||
    isDamageLikeName(traitName) ||
    (typeof isTotalDamageName === 'function' && isTotalDamageName(traitName)) ||
    (typeof isDpsDamageName === 'function' && isDpsDamageName(traitName)) ||
    (typeof isMinTotalDamageName === 'function' && isMinTotalDamageName(traitName)) ||
    (typeof isMinDpsDamageName === 'function' && isMinDpsDamageName(traitName));
  if (isMagnitudeBased) {
    let s;
    if (raw >= 1e12) s = (raw / 1e12).toFixed(2) + '조';
    else if (raw >= 1e8) s = (raw / 1e8).toFixed(2) + '억';
    else if (raw >= 1e4) s = (raw / 1e4).toFixed(2) + '만';
    else s = raw.toFixed(2);
    s = s.replace(/\.00(조|억|만)?$/, '$1');
    return s;
  }
  let s = raw.toFixed(2);
  s = s.replace(/\.00$/, '');
  return s;
}
function calcTraitAverages(grouped) {
  const result = [];
  Object.keys(grouped).forEach(name => {
    const arr = grouped[name];
    if (!arr.length) return;
    const sum = arr.reduce((s, v) => s + (Number.isFinite(v.raw) ? v.raw : 0), 0);
    const count = arr.length;
    const avgRaw = count ? sum / count : 0;
    result.push({ trait: name, value: formatByUnitForDisplay(avgRaw, name) });
  });
  return result;
}
function computeTraitAveragesFromRecords(records) {
  const grouped = groupTraitsByName(records);
  const averages = calcTraitAverages(grouped);
  return averages.sort((a, b) => a.trait.localeCompare(b.trait));
}


/* =========================================================
 * 한글 OCR 보조 유틸(자모/초성 기반 매칭)
 * ======================================================= */
const KEEP_HANGUL = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7A3\uA960-\uA97F\uD7B0-\uD7FF]/g;
function filterByCandidateChars(ocr, candidate) {
  const set = new Set((candidate.match(KEEP_HANGUL) || []));
  return (ocr.match(KEEP_HANGUL) || []).filter(ch => set.has(ch)).join('');
}
function lcsRatio(a, b) {
  const m = a.length, n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] + 1 : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  return n ? dp[m][n] / n : 0;
}
function choseongSeq(s) {
  const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
  return Array.from(s).map(ch => {
    const code = ch.charCodeAt(0);
    if (code < 0xAC00 || code > 0xD7A3) return ch;
    const idx = code - 0xAC00;
    const cho = Math.floor(idx / 588);
    return CHO[cho];
  }).join('');
}
function jamoSim(a, b) {
  const aa = choseongSeq(a), bb = choseongSeq(b);
  return lcsRatio(aa, bb);
}
function bestEngravingMatch(ocrRaw, candidates) {
  const ocr = (ocrRaw || '').match(KEEP_HANGUL)?.join('') ?? '';
  let best = candidates[0] || '', bestScore = 0;
  for (const cand of candidates) {
    const filtered = filterByCandidateChars(ocr, cand);
    const sLCS = lcsRatio(filtered, cand);
    const sJamo = jamoSim(filtered, cand);
    const sLev = similarity(filtered, cand);
    const score = 0.55 * sLCS + 0.30 * sJamo + 0.15 * sLev;
    if (score > bestScore) { bestScore = score; best = cand; }
  }
  const accept = bestScore >= 0.5;
  return { best: accept ? best : '', score: bestScore };
}


/* =========================================================
 * 내부 소형 UI 컴포넌트 (FAB/BottomSheet)
 * ======================================================= */
function MobileSavedRaidsFab({ onOpen, shown }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={[
        "lg:hidden fixed bottom-4 right-4 z-40 rounded-full shadow-lg",
        "bg-indigo-600 text-white px-4 py-3 text-sm",
        "transition-all",
        "transform will-change-transform",
        shown
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-2 pointer-events-none"
      ].join(" ")}
      aria-label="저장된 기록 열기"
    >
      저장된 기록
    </button>
  );
}
function MobileBottomSheet({ open, onClose, children }) {
  return (
    <div className={`lg:hidden fixed inset-0 z-40 ${open ? '' : 'pointer-events-none'}`}>
      <div
        className={[
          "absolute inset-0 bg-black/40",
          "transition-opacity duration-300 ease-out",
          open ? "opacity-100" : "opacity-0"
        ].join(' ')}
        onClick={onClose}
      />
      <div
        className={[
          "absolute inset-x-0 bottom-0 h-[65vh] rounded-t-2xl bg-white dark:bg-zinc-900 shadow-2xl flex flex-col flex-1 basis-[0px] min-h-0",
          "transition-transform will-change-transform",
          open ? "translate-y-0" : "translate-y-full"
        ].join(' ')}
      >
        <div className="mx-auto mt-2 mb-1 h-1.5 w-10 rounded-full bg-zinc-300 dark:bg-zinc-700" />
        <div className="p-3 overflow-y-auto flex flex-col flex-1 basis-[0px] min-h-0 ">{children}</div>
      </div>
    </div>
  );
}


/* =========================================================
 * 메인 컴포넌트
 * ======================================================= */
export default function BattleAnalyzer() {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);

  const { user, ready } = useAuth();
  const { user: authUser } = useAuth();

  const [stream, setStream] = useState(null);
  const [profileInfo, setProfileInfo] = useState({ job: '', jobEngraving: '', level: '', power: '' });
  const [battleMainData, setBattleMainData] = useState({});
  const [step, setStep] = useState('idle');
  const [viewMode, setViewMode] = useState('live');
  const [loadedId, setLoadedId] = useState(null);

  const [progressByStep, setProgressByStep] = useState({
    profile: { done: 0, total: 0 },
    battleMain: { done: 0, total: 0 },
    battleMain2: { done: 0, total: 0 },
  });

  const { addCount: _addCountForUI } = getJobConfig(profileInfo.job || '알수없음', profileInfo.jobEngraving || '');
  const showPage2Progress = _addCountForUI > 14;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [memo, setMemo] = useState('');
  const partialRef = useRef(null);
  const [bigView, setBigView] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [mobileSavedOpen, setMobileSavedOpen] = useState(false);

  const [fabShown, setFabShown] = useState(true);
  const forceVisibleRef = useRef(0);
  const hideTimerRef = useRef(null);
  const HIDE_DELAY = 5000;
  const END_THRESHOLD = 56;

  const isAtEnd = () => {
    const se = document.scrollingElement || document.documentElement || document.body;
    const viewportBottom = (se.scrollTop || 0) + (window.innerHeight || 0);
    const pageBottom = (se.scrollHeight || 0);
    return viewportBottom >= pageBottom - END_THRESHOLD;
  };

  useEffect(() => {
    setFabShown(true);
    hideTimerRef.current && clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setFabShown(!isAtEnd());
    }, 1400);

    const recalcByScroll = () => {
      const forced = Date.now() < forceVisibleRef.current;
      if (forced) return;
      setFabShown(!isAtEnd());
    };

    const onUserInteract = () => {
      setFabShown(true);
      forceVisibleRef.current = Date.now() + HIDE_DELAY;

      hideTimerRef.current && clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => {
        forceVisibleRef.current = 0;
        setFabShown(!isAtEnd());
      }, HIDE_DELAY);
    };

    window.addEventListener('scroll', recalcByScroll, { passive: true });
    window.addEventListener('pointerdown', onUserInteract, { passive: true });
    window.addEventListener('touchstart', onUserInteract, { passive: true });
    window.addEventListener('mousedown', onUserInteract, { passive: true });

    return () => {
      window.removeEventListener('scroll', recalcByScroll);
      window.removeEventListener('pointerdown', onUserInteract);
      window.removeEventListener('touchstart', onUserInteract);
      window.removeEventListener('mousedown', onUserInteract);
      hideTimerRef.current && clearTimeout(hideTimerRef.current);
    };
  }, []);

  const [savedRaids, setSavedRaids] = useState(() => {
    try {
      const saved = localStorage.getItem('savedRaids');
      const parsed = saved ? JSON.parse(saved) : [];
      return parsed.map(r => ({ ...r, _local: true, source: 'local' }));
    } catch {
      return [];
    }
  });

  let loginHint = '';
  if (authUser?.uid) {
    if (savedRaids.length === 0) {
      loginHint =
        '클라우드에 저장된 전투 분석 기록이 없습니다.\n(모바일에서는 화면 공유 기능이 제한되므로, PC에서 전투 분석 기록을 저장한 후 이용할 수 있습니다.)';
    } else {
      loginHint =
        '오른쪽 하단 저장된 기록 버튼을 눌러 저장된 전투 분석 기록을 불러오세요.';
    }
  } else {
    loginHint =
      '좌측 메뉴에서 Google 로그인 후 클라우드에 저장된 전투 분석 기록을 불러오세요.';
  }

  const refreshCloudMerge = React.useCallback(async () => {
    try {
      if (!authUser?.uid) {
        setSavedRaids(prev => prev.filter(r => r._local || r.source === 'local'));
        return;
      }
      const cloud = (await getMyRaids(authUser.uid)).map(r => ({
        ...r,
        _cloud: true,
        _local: false,
        source: 'cloud',
      }));
      setSavedRaids(prev => {
        const localOnly = prev
          .filter(r => r._local || r.source === 'local')
          .map(r => ({ ...r, _local: true, _cloud: false, source: 'local' }));
        return mergeLocalAndCloud(localOnly, cloud);
      });
    } catch (e) {
      console.error('[refreshCloudMerge error]', e);
    }
  }, [authUser?.uid]);

  useEffect(() => {
    if (!ready) return;
    refreshCloudMerge();
  }, [ready, authUser?.uid, refreshCloudMerge]);

  const formatYMD = (ts) => {
    const s = String(ts || '');
    const m = s.replace(/[^\d]/g, ' ').trim().split(/\s+/);
    if (m.length < 3) return '';
    const [y, mo, da] = m;
    const z2 = v => String(v).padStart(2, '0');
    return `${y}-${z2(mo)}-${z2(da)}`;
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [averages, setAverages] = useState({ main: [], add: [] });
  const [selectionStamp] = useState(0);
  const [, setLoadedSelectionStamp] = useState(-1);
  const [avgSnapshot, setAvgSnapshot] = useState(null);
  const [compareSnapshot, setCompareSnapshot] = useState(null);
  const [badgeIds, setBadgeIds] = useState([]);

  useEffect(() => {
    const localOnly = (savedRaids || []).filter(r => r._local || r.source === 'local');
    localStorage.setItem('savedRaids', JSON.stringify(localOnly));
  }, [savedRaids]);

  useEffect(() => {
    if (ready && !authUser?.uid) {
      setSavedRaids(prev => prev.filter(r => r._local));
    }
  }, [ready, authUser?.uid]);

  const suppressDisplay = false;

  /* ---------- 진행률 상태 도우미 ---------- */
  function setStepPlan(stepKey, total) {
    setProgressByStep(prev => ({
      ...prev,
      [stepKey]: { done: 0, total: Math.max(total, 0) }
    }));
    setLoading(true);
    setError(null);
  }
  function bumpStepProgress(stepKey, inc = 1) {
    setProgressByStep(prev => {
      const cur = prev[stepKey] || { done: 0, total: 0 };
      const nextDone = Math.min(cur.total, cur.done + inc);
      return { ...prev, [stepKey]: { ...cur, done: nextDone } };
    });
  }
  function getStepPercent(stepKey) {
    const { done, total } = progressByStep[stepKey] || { done: 0, total: 0 };
    return total ? Math.round((done / total) * 100) : 0;
  }

  /* ---------- 비디오/컨텐츠 좌표 변환 ---------- */
  const CONTENT_BASE_W = 1920;
  const CONTENT_BASE_H = 1080;
  const video = videoRef.current;
  // 컨텐츠는 좌우 1px, 아래 0px, 위는 가변 (stable)
  const getFrame = useCallback((videoEl) => {
    const LEFT = 1;
    const RIGHT = 1;
    const BOTTOM = 0;
    const contentH = ((videoEl.videoWidth - (LEFT + RIGHT)) / 16) * 9;
    const TOP = Math.max(0, videoEl.videoHeight - contentH - BOTTOM);
    return { LEFT, RIGHT, BOTTOM, TOP };
  }, []);
  function roiFullToContent(r) {
    return { x: r.x, y: r.y, w: r.w, h: r.h };
  }
  const getContentGeom = useCallback((videoEl) => {
    const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
    const FRAME = getFrame(videoEl);
    const cw = Math.max(0, vw - (FRAME.LEFT + FRAME.RIGHT));
    const ch = Math.max(0, vh - (FRAME.TOP + FRAME.BOTTOM));
    const scaleX = cw / CONTENT_BASE_W; // 1920 → 실제 컨텐츠 폭
    const scaleY = ch / CONTENT_BASE_H; // 1080 → 실제 컨텐츠 높이
    return { scaleX, scaleY, ox: FRAME.LEFT, oy: FRAME.TOP, FRAME };
  }, [getFrame, CONTENT_BASE_W, CONTENT_BASE_H]);
  function getVideoElementScale(videoEl) {
    if (!videoEl) return { sx: 1, sy: 1 };
    const sx = (videoEl.clientWidth || 1) / (videoEl.videoWidth || 1);
    const sy = (videoEl.clientHeight || 1) / (videoEl.videoHeight || 1);
    return { sx, sy };
  }
  const collectVisibleROIs = useCallback((curStep, job) => {
    const list = [];
    const engr = (battleMainData.jobEngraving || profileInfo.jobEngraving || '');
    if (curStep === 'profile') {
      ['job', 'jobEngraving', 'level', 'power'].forEach(k => list.push({ key: k, r: ROI[k], kind: 'profile' }));
    } else if (curStep === 'battleMain') {
      ['raidName', 'battleTime', 'timestamp'].forEach(k => list.push({ key: k, r: ROI[k], kind: 'meta' }));
      const { mainCount, addCount } = getJobConfig(job || '알수없음', engr);
      for (let i = 1; i <= mainCount; i++) {
        list.push({ key: `mainTrait${i}`, r: ROI[`mainTrait${i}`], kind: 'main' });
        list.push({ key: `mainTrait${i}Value`, r: ROI[`mainTrait${i}Value`], kind: 'value' });
      }
      const firstPageEnd = Math.min(14, addCount);
      for (let i = 1; i <= firstPageEnd; i++) {
        list.push({ key: `addTrait${i}`, r: ROI[`addTrait${i}`], kind: 'add' });
        list.push({ key: `addTrait${i}Value`, r: ROI[`addTrait${i}Value`], kind: 'value' });
      }
    } else if (curStep === 'battleMain2') {
      const { addCount } = getJobConfig(job || '알수없음', engr);
      const map = (addCount <= 16) ? ROI_SECOND_PASS_16 : ROI_SECOND_PASS_18;
      const startIdx = 15, endIdx = Math.min(addCount, 18);
      for (let i = startIdx; i <= endIdx; i++) {
        list.push({ key: `addTrait${i}`, r: map[`addTrait${i}`], kind: 'add' });
        list.push({ key: `addTrait${i}Value`, r: map[`addTrait${i}Value`], kind: 'value' });
      }
    } else if (curStep === 'done') {
      return collectVisibleROIs('battleMain', job);
    }
    return list.filter(v => v.r);
  }, [battleMainData.jobEngraving, profileInfo.jobEngraving]);
  function roundedRectPath(ctx, x, y, w, h, r = 1) {
    const rr = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.lineTo(x + w - rr, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
    ctx.lineTo(x + w, y + h - rr);
    ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    ctx.lineTo(x + rr, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
    ctx.lineTo(x, y + rr);
    ctx.quadraticCurveTo(x, y, x + rr, y);
    ctx.closePath();
  }
  function highlightFill(kind) {
    switch (kind) {
      case 'profile': return 'rgba(255, 215, 0, 0.18)';
      case 'meta': return 'rgba(255, 165, 0, 0.18)';
      case 'main': return 'rgba(46, 204, 113, 0.18)';
      case 'add': return 'rgba(155, 89, 182, 0.18)';
      case 'value': return 'rgba(231, 76, 60, 0.18)';
      default: return 'rgba(255, 255, 0, 0.18)';
    }
  }

  /* ---------- 바이너리 팽창(얇은 획 보정) ---------- */
  function dilateOnce(gd, canvas) {
    let dark = 0, light = 0;
    for (let i = 0; i < gd.length; i += 4) {
      if (gd[i] < 128) dark++;
      else light++;
    }
    const inkVal = (light < dark) ? 255 : 0;
    const W = canvas.width, H = canvas.height;
    const src = new Uint8ClampedArray(gd);
    const idx = (x, y) => (y * W + x) * 4;

    for (let y = 0; y < H - 1; y++) {
      for (let x = 0; x < W - 1; x++) {
        const hasInk =
          src[idx(x, y)] === inkVal ||
          src[idx(x + 1, y)] === inkVal ||
          src[idx(x, y + 1)] === inkVal ||
          src[idx(x + 1, y + 1)] === inkVal;

        const o = idx(x, y);
        const v = hasInk ? inkVal : (inkVal ^ 255);
        gd[o] = gd[o + 1] = gd[o + 2] = v;
        gd[o + 3] = 255;
      }
    }
  }

  /* ---------- 하이라이트/디버그 오버레이 ---------- */
  const drawOverlay = useCallback(() => {
    const video = videoRef.current;
    const canvas = overlayRef.current;
    if (!canvas) return;
    const targetW = video ? (video.clientWidth || 1) : (canvas.clientWidth || 1);
    const targetH = video ? (video.clientHeight || 1) : (canvas.clientHeight || 1);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (!video || !video.videoWidth || !video.videoHeight) return;
    if (!showOverlay) return;
    const w = video.clientWidth || 1;
    const h = video.clientHeight || 1;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    ctx.lineWidth = 2;
    const { scaleX, scaleY, ox, oy, FRAME } = getContentGeom(video);
    const { sx: ex, sy: ey } = getVideoElementScale(video);
    const job = profileInfo.job || '알수없음';
    const list = collectVisibleROIs(step, job);
    list.forEach(({ r, kind }) => {
      const rc = roiFullToContent(r);
      const x_v = ox + rc.x * scaleX;
      const y_v = oy + rc.y * scaleY;
      const w_v = rc.w * scaleX;
      const h_v = rc.h * scaleY;
      const x = Math.round(x_v * ex);
      const y = Math.round(y_v * ey);
      const ww = Math.round(w_v * ex);
      const hh = Math.round(h_v * ey);
      ctx.save();
      ctx.fillStyle = highlightFill(kind);
      roundedRectPath(ctx, x, y, ww, hh, 3);
      ctx.fill();
      ctx.restore();
    });
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    const cw = Math.max(0, vw - (FRAME.LEFT + FRAME.RIGHT));
    const ch = Math.max(0, vh - (FRAME.TOP + FRAME.BOTTOM));
    const lines = [
      `Video:   ${vw} × ${vh}`,
      `Content: ${Math.round(cw)} × ${Math.round(ch)}`,
      `Client:  ${w} × ${h}`,
    ];
    ctx.save();
    ctx.font = '12px sans-serif';
    const pad = 6;
    const lineH = 16;
    const boxW = Math.max(...lines.map(t => ctx.measureText(t).width)) + pad * 2;
    const boxH = lineH * lines.length + pad * 2;
    const boxX = 8;
    const boxY = 8;
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.fillRect(boxX, boxY, boxW, boxH);
    ctx.fillStyle = '#fff';
    lines.forEach((t, i) => {
      ctx.fillText(t, boxX + pad, boxY + pad + lineH * (i + 0.8));
    });
    ctx.restore();
  }, [step, profileInfo.job, showOverlay, collectVisibleROIs, getContentGeom]);
  /* ---------- ROI 캡처(일반/특수) ---------- */
  function captureROIBase64(roi, special = false) {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(roi);
    const sx = Math.round(ox + rc.x * scaleX);
    const sy = Math.round(oy + rc.y * scaleY);
    const sw = Math.round(rc.w * scaleX);
    const sh = Math.round(rc.h * scaleY);
    const up = 1;
    const canvas = document.createElement('canvas');
    canvas.width = sw * up;
    canvas.height = sh * up;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, sw * up, sh * up);

    if (roi === MYCHAR_ROI) {
      // eslint-disable-next-line no-unused-vars
      const clamp = (v, lo = 0, hi = 255) => (v < lo ? lo : v > hi ? hi : v) | 0;

      const gaussianKernel1D = (sigma) => {
        const s = Math.max(0.8, sigma || 1.0);
        const radius = Math.max(1, Math.floor(s * 3));
        const size = radius * 2 + 1;
        const k = new Float32Array(size);
        const denom = 2 * s * s;
        let sum = 0;
        for (let i = -radius, idx = 0; i <= radius; i++, idx++) {
          const v = Math.exp(-(i * i) / denom);
          k[idx] = v; sum += v;
        }
        for (let i = 0; i < size; i++) k[i] /= sum;
        return { k, radius };
      };
      const gaussianBlur = (src, w, h, sigma) => {
        const { k, radius } = gaussianKernel1D(sigma);
        const tmp = new Uint8ClampedArray(src.length);
        const out = new Uint8ClampedArray(src.length);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const xx = Math.min(w - 1, Math.max(0, x + t));
              const i = (y * w + xx) * 4;
              const kv = k[t + radius];
              r += src[i] * kv; g += src[i + 1] * kv; b += src[i + 2] * kv; a += src[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
          }
        }
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const yy = Math.min(h - 1, Math.max(0, y + t));
              const i = (yy * w + x) * 4;
              const kv = k[t + radius];
              r += tmp[i] * kv; g += tmp[i + 1] * kv; b += tmp[i + 2] * kv; a += tmp[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
          }
        }
        return out;
      };
      const otsu = (hist, total) => {
        let sum = 0;
        for (let t = 0; t < 256; t++) sum += t * hist[t];
        let sumB = 0, wB = 0, varMax = 0, thr = 127;
        for (let t = 0; t < 256; t++) {
          wB += hist[t];
          if (!wB) continue;
          const wF = total - wB; if (!wF) break;
          sumB += t * hist[t];
          const mB = sumB / wB, mF = (sum - sumB) / wF;
          const v = wB * wF * (mB - mF) ** 2;
          if (v > varMax) { varMax = v; thr = t; }
        }
        return thr;
      };
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = img.data;
      const W = canvas.width, H = canvas.height;

      (function levels({ inBlack, inWhite, gamma, outBlack, outWhite }) {
        const invRange = 1 / Math.max(1, (inWhite - inBlack));
        const gammaPow = 1 / Math.max(1e-6, gamma);
        for (let i = 0; i < d.length; i += 4) {
          for (let c = 0; c < 3; c++) {
            let v = d[i + c];
            const t = Math.min(1, Math.max(0, (v - inBlack) * invRange));
            v = outBlack + (outWhite - outBlack) * Math.pow(t, gammaPow);
            d[i + c] = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          }
        }
      })({ inBlack: 86, inWhite: 165, gamma: 0.1, outBlack: 7, outWhite: 153 });

      (function channelAdjust({ rGain, gGain, bGain, rOffset, gOffset, bOffset }) {
        for (let i = 0; i < d.length; i += 4) {
          d[i]     = (d[i]     * rGain + rOffset) | 0;
          d[i + 1] = (d[i + 1] * gGain + gOffset) | 0;
          d[i + 2] = (d[i + 2] * bGain + bOffset) | 0;
        }
      })({ rGain: 0.77, gGain: 1, bGain: 1.73, rOffset: -255, gOffset: 2, bOffset: 0 });

      (function grayscale() {
        for (let i = 0; i < d.length; i += 4) {
          const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const v = (gray < 0 ? 0 : gray > 255 ? 255 : gray) | 0;
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      (function brightnessContrast({ brightness, contrast }) {
        const intercept = 128 * (1 - contrast) + brightness;
        for (let i = 0; i < d.length; i += 4) {
          const v = (d[i] * contrast + intercept) | 0;
          const vv = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          d[i] = d[i + 1] = d[i + 2] = vv;
          d[i + 3] = 255;
        }
      })({ brightness: 255, contrast: 4 });

      (function invert() {
        for (let i = 0; i < d.length; i += 4) {
          d[i] = 255 - d[i];
          d[i + 1] = 255 - d[i + 1];
          d[i + 2] = 255 - d[i + 2];
        }
      })();

      (function softThreshold({ softness, shift }) {
        const hist = new Uint32Array(256);
        for (let i = 0; i < d.length; i += 4) hist[d[i]]++;
        const total = W * H;
        let thr = otsu(hist, total);
        thr = Math.max(0, Math.min(255, thr + shift));
        for (let i = 0; i < d.length; i += 4) {
          const diff = d[i] - thr;
          let v;
          if (Math.abs(diff) >= softness) {
            v = diff > 0 ? 255 : 0;
          } else {
            v = (((diff + softness) / (2 * softness)) * 255) | 0;
          }
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })({ softness: 104, shift: -64 });

      const UNSHARP_ENABLED = false;
      if (UNSHARP_ENABLED) {
        (function unsharpMask({ amount, sigma, threshold }) {
          const src = new Uint8ClampedArray(d);
          const blurred = gaussianBlur(src, W, H, sigma);
          for (let i = 0; i < d.length; i += 4) {
            const hr = src[i] - blurred[i];
            const hg = src[i + 1] - blurred[i + 1];
            const hb = src[i + 2] - blurred[i + 2];
            const mag = Math.max(Math.abs(hr), Math.abs(hg), Math.abs(hb));
            if (mag > threshold) {
              d[i]     = (src[i]     + amount * hr) | 0;
              d[i + 1] = (src[i + 1] + amount * hg) | 0;
              d[i + 2] = (src[i + 2] + amount * hb) | 0;
            } else {
              d[i]     = src[i];
              d[i + 1] = src[i + 1];
              d[i + 2] = src[i + 2];
            }
          }
        })({ amount: 1.2, sigma: 1.2, threshold: 2 });
      }

      ctx.putImageData(img, 0, 0);
      return canvas.toDataURL('image/png');
    } else if (special) {
      // eslint-disable-next-line no-unused-vars
      const clamp = (v, lo = 0, hi = 255) => (v < lo ? lo : v > hi ? hi : v) | 0;

      const gaussianKernel1D = (sigma) => {
        const s = Math.max(0.8, sigma || 1.0);
        const radius = Math.max(1, Math.floor(s * 3));
        const size = radius * 2 + 1;
        const k = new Float32Array(size);
        const denom = 2 * s * s;
        let sum = 0;
        for (let i = -radius, idx = 0; i <= radius; i++, idx++) {
          const v = Math.exp(-(i * i) / denom);
          k[idx] = v; sum += v;
        }
        for (let i = 0; i < size; i++) k[i] /= sum;
        return { k, radius };
      };
      const gaussianBlur = (src, w, h, sigma) => {
        const { k, radius } = gaussianKernel1D(sigma);
        const tmp = new Uint8ClampedArray(src.length);
        const out = new Uint8ClampedArray(src.length);
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const xx = Math.min(w - 1, Math.max(0, x + t));
              const i = (y * w + xx) * 4;
              const kv = k[t + radius];
              r += src[i] * kv;
              g += src[i + 1] * kv;
              b += src[i + 2] * kv;
              a += src[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            tmp[o] = r; tmp[o + 1] = g; tmp[o + 2] = b; tmp[o + 3] = a;
          }
        }
        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            let r = 0, g = 0, b = 0, a = 0;
            for (let t = -radius; t <= radius; t++) {
              const yy = Math.min(h - 1, Math.max(0, y + t));
              const i = (yy * w + x) * 4;
              const kv = k[t + radius];
              r += tmp[i] * kv;
              g += tmp[i + 1] * kv;
              b += tmp[i + 2] * kv;
              a += tmp[i + 3] * kv;
            }
            const o = (y * w + x) * 4;
            out[o] = r; out[o + 1] = g; out[o + 2] = b; out[o + 3] = a;
          }
        }
        return out;
      };
      const otsu = (hist, total) => {
        let sum = 0;
        for (let t = 0; t < 256; t++) sum += t * hist[t];
        let sumB = 0, wB = 0, varMax = 0, thr = 127;
        for (let t = 0; t < 256; t++) {
          wB += hist[t];
          if (!wB) continue;
          const wF = total - wB;
          if (!wF) break;
          sumB += t * hist[t];
          const mB = sumB / wB, mF = (sum - sumB) / wF;
          const val = wB * wF * (mB - mF) ** 2;
          if (val > varMax) { varMax = val; thr = t; }
        }
        return thr;
      };

      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = img.data;
      const W = canvas.width, H = canvas.height;

      (function unsharpMask(amount = 1.95, sigma = 3.8, threshold = 22) {
        const src = new Uint8ClampedArray(d);
        const blurred = gaussianBlur(src, W, H, sigma);
        for (let i = 0; i < d.length; i += 4) {
          const hr = src[i] - blurred[i];
          const hg = src[i + 1] - blurred[i + 1];
          const hb = src[i + 2] - blurred[i + 2];
          const mag = Math.max(Math.abs(hr), Math.abs(hg), Math.abs(hb));
          if (mag > threshold) {
            d[i] = (src[i] + amount * hr) | 0;
            d[i + 1] = (src[i + 1] + amount * hg) | 0;
            d[i + 2] = (src[i + 2] + amount * hb) | 0;
          } else {
            d[i] = src[i];
            d[i + 1] = src[i + 1];
            d[i + 2] = src[i + 2];
          }
        }
      })();

      (function channelAdjust() {
        const rG = 0.35, gG = 1.0, bG = 0.43;
        const rO = -63, gO = -4, bO = -63;
        for (let i = 0; i < d.length; i += 4) {
          d[i] = (d[i] * rG + rO) | 0;
          d[i + 1] = (d[i + 1] * gG + gO) | 0;
          d[i + 2] = (d[i + 2] * bG + bO) | 0;
        }
      })();

      (function grayscale() {
        for (let i = 0; i < d.length; i += 4) {
          const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
          const v = (gray < 0 ? 0 : gray > 255 ? 255 : gray) | 0;
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      (function brightnessContrast() {
        const brightness = 32;
        const contrast = 3.0;
        const intercept = 128 * (1 - contrast) + brightness;
        for (let i = 0; i < d.length; i += 4) {
          const v = (d[i] * contrast + intercept) | 0;
          const vv = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          d[i] = d[i + 1] = d[i + 2] = vv;
          d[i + 3] = 255;
        }
      })();

      (function levels() {
        const inBlack = 30, inWhite = 300, gamma = 0.5, outBlack = 0, outWhite = 255;
        const invRange = 1 / Math.max(1, (inWhite - inBlack));
        const gammaPow = 1 / Math.max(1e-6, gamma);
        for (let i = 0; i < d.length; i += 4) {
          for (let c = 0; c < 3; c++) {
            let v = d[i + c];
            const t = Math.min(1, Math.max(0, (v - inBlack) * invRange));
            v = outBlack + (outWhite - outBlack) * Math.pow(t, gammaPow);
            d[i + c] = (v < 0 ? 0 : v > 255 ? 255 : v) | 0;
          }
        }
      })();

      (function invert() {
        for (let i = 0; i < d.length; i += 4) {
          d[i] = 255 - d[i];
          d[i + 1] = 255 - d[i + 1];
          d[i + 2] = 255 - d[i + 2];
        }
      })();

      (function softThreshold() {
        const softness = 33;
        const shift = -15;
        const hist = new Uint32Array(256);
        for (let i = 0; i < d.length; i += 4) hist[d[i]]++;
        const total = W * H;
        let thr = otsu(hist, total);
        thr = Math.max(0, Math.min(255, thr + shift));
        for (let i = 0; i < d.length; i += 4) {
          const diff = d[i] - thr;
          let v;
          if (Math.abs(diff) >= softness) {
            v = diff > 0 ? 255 : 0;
          } else {
            v = (((diff + softness) / (2 * softness)) * 255) | 0;
          }
          d[i] = d[i + 1] = d[i + 2] = v;
        }
      })();

      if (roi === ROI.level || roi?.id === 'level') {
        (function verticalEnhance() {
          const src = new Uint8ClampedArray(d);
          const idx = (x, y) => (y * W + x) * 4;
          for (let y = 1; y < H - 1; y++) {
            for (let x = 0; x < W; x++) {
              const i0 = idx(x, y + 1);
              const i1 = idx(x, y);
              const i2 = idx(x, y + 1);
              const m = Math.max(src[i0], src[i1], src[i2]);
              d[i1] = d[i1 + 1] = d[i1 + 2] = m;
            }
          }
        })();
      }

      dilateOnce(img.data, canvas);
      ctx.putImageData(img, 0, 0);
    }
    return canvas.toDataURL('image/png');
  }

  /* ---------- 직업 각인 ROI 캡처 ---------- */
  function captureJobEngravingROI(invert = true) {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(ROI.jobEngraving);
    const sx = Math.round(ox + rc.x * scaleX);
    const sy = Math.round(oy + rc.y * scaleY);
    const sw = Math.round(rc.w * scaleX);
    const sh = Math.round(rc.h * scaleY);
    const up = 3;
    const canvas = document.createElement('canvas');
    canvas.width = sw * up;
    canvas.height = sh * up;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, sw * up, sh * up);

    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;

    const contrast = 2.1, brightness = 0;
    const intercept = 128 * (1 - contrast) + brightness;
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i] * contrast + intercept;
      let g = d[i + 1] * contrast + intercept;
      let b = d[i + 2] * contrast + intercept;
      d[i] = Math.min(255, Math.max(0, r));
      d[i + 1] = Math.min(255, Math.max(0, g));
      d[i + 2] = Math.min(255, Math.max(0, b));
    }
    ctx.putImageData(img, 0, 0);

    const gimg = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gd = gimg.data;
    const hist = new Array(256).fill(0);
    for (let i = 0; i < gd.length; i += 4) {
      const g = 0.299 * gd[i] + 0.587 * gd[i + 1] + 0.114 * gd[i + 2];
      gd[i] = gd[i + 1] = gd[i + 2] = g;
      hist[Math.round(g)]++;
    }
    const total = canvas.width * canvas.height;
    let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, varMax = 0, thr = 127;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      const wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB, mF = (sum - sumB) / wF;
      const val = wB * wF * (mB - mF) ** 2;
      if (val > varMax) { varMax = val; thr = t; }
    }
    const softness = 50;
    for (let i = 0; i < gd.length; i += 4) {
      const g = gd[i];
      const dff = g - (thr - 5);
      let v;
      if (Math.abs(dff) >= softness) v = dff > 0 ? 255 : 0;
      else v = Math.round(((dff + softness) / (2 * softness)) * 255);
      const out = invert ? 255 - v : v;
      gd[i] = gd[i + 1] = gd[i + 2] = out;
      gd[i + 3] = 255;
    }
    ctx.putImageData(gimg, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /* ---------- 전투 화면 ROI 캡처(값/이름/메타) ---------- */
  function captureBattleROI(roi, mode = 'value') {
    const v = videoRef.current;
    if (!v || !v.videoWidth || !v.videoHeight) return null;
    const { scaleX, scaleY, ox, oy } = getContentGeom(video);
    const rc = roiFullToContent(roi);
    let sx = Math.round(ox + rc.x * scaleX);
    let sy = Math.round(oy + rc.y * scaleY);
    let sw = Math.round(rc.w * scaleX);
    let sh = Math.round(rc.h * scaleY);
    const pad = (mode === 'value') ? 3 : 1;
    sx = Math.max(0, sx - pad);
    sy = Math.max(0, sy - pad);
    sw = Math.min(v.videoWidth - sx, sw + pad * 2);
    sh = Math.min(v.videoHeight - sy, sh + pad * 2);

    const up = 2;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, sw * up);
    canvas.height = Math.max(1, sh * up);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(v, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    const preset = (() => {
      if (mode === 'value') {
        return {
          contrast: 1,
          brightness: 0,
          invert: true,
          softness: 25,
          otsuShift: -10,
          redScale: 1.0,
          blueScale: 1.0,
          gamma: 1,
          inBlack: 0,
          inWhite: 255
        };
      }
      if (mode === 'name') {
        return {
          contrast: 1.0,
          brightness: 0,
          invert: true,
          softness: 50,
          otsuShift: 0,
          redScale: 0.9,
          blueScale: 0.9,
          gamma: 1,
          inBlack: 30,
          inWhite: 255
        };
      }
      return {
        contrast: 1.0,
        brightness: 0,
        invert: true,
        softness: 100,
        otsuShift: 0,
        redScale: 0.9,
        blueScale: 0.9,
        gamma: 1,
        inBlack: 30,
        inWhite: 255
      };
    })();

    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;
    const invRange = 1 / Math.max(1, (preset.inWhite - preset.inBlack));
    const gammaPow = 1 / Math.max(0.01, preset.gamma);
    for (let i = 0; i < d.length; i += 4) {
      let r = d[i], g = d[i + 1], b = d[i + 2];
      const applyLevel = (c) =>
        Math.pow(Math.min(1, Math.max(0, (c - preset.inBlack) * invRange)), gammaPow) * 255;
      r = applyLevel(r);
      g = applyLevel(g);
      b = applyLevel(b);
      r *= preset.redScale;
      b *= preset.blueScale;
      d[i] = r; d[i + 1] = g; d[i + 2] = b;
    }
    ctx.putImageData(img, 0, 0);

    const gray = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const gd = gray.data;
    for (let i = 0; i < gd.length; i += 4) {
      const gr = 0.299 * gd[i] + 0.587 * gd[i + 1] + 0.114 * gd[i + 2];
      gd[i] = gd[i + 1] = gd[i + 2] = gr;
    }

    const cf = preset.contrast;
    const intercept = 128 * (1 - cf) + preset.brightness;
    for (let i = 0; i < gd.length; i += 4) {
      let v = gd[i] * cf + intercept;
      v = Math.max(0, Math.min(255, v));
      gd[i] = gd[i + 1] = gd[i + 2] = v;
    }

    if (preset.invert) {
      for (let i = 0; i < gd.length; i += 4) {
        gd[i] = 255 - gd[i];
        gd[i + 1] = 255 - gd[i + 1];
        gd[i + 2] = 255 - gd[i + 2];
      }
    }

    const hist = new Array(256).fill(0);
    for (let i = 0; i < gd.length; i += 4) hist[gd[i] | 0]++;
    const total = canvas.width * canvas.height;
    let sum = 0; for (let t = 0; t < 256; t++) sum += t * hist[t];
    let sumB = 0, wB = 0, varMax = 0, thr = 127;
    for (let t = 0; t < 256; t++) {
      wB += hist[t]; if (!wB) continue;
      const wF = total - wB; if (!wF) break;
      sumB += t * hist[t];
      const mB = sumB / wB, mF = (sum - sumB) / wF;
      const between = wB * wF * (mB - mF) ** 2;
      if (between > varMax) { varMax = between; thr = t; }
    }
    thr = Math.max(0, Math.min(255, thr + (preset.otsuShift || 0)));
    const softness = preset.softness;
    for (let i = 0; i < gd.length; i += 4) {
      const diff = gd[i] - thr;
      let v;
      if (Math.abs(diff) >= softness) v = diff > 0 ? 255 : 0;
      else v = Math.round(((diff + softness) / (2 * softness)) * 255);
      gd[i] = gd[i + 1] = gd[i + 2] = v;
      gd[i + 3] = 255;
    }
    ctx.putImageData(gray, 0, 0);
    return canvas.toDataURL('image/png');
  }

  /* ---------- OCR 실행 래퍼 ---------- */
  async function runOCR(img, { lang = 'kor+eng', whitelist = '', psm = null } = {}, onStep = null) {
    if (!img) return '';
    const opts = { logger: () => { } };
    if (whitelist) opts.tessedit_char_whitelist = whitelist;
    if (psm !== null && psm !== undefined) opts.psm = psm;
    const { data: { text } } = await Tesseract.recognize(img, lang, opts);
    if (onStep) onStep();
    return (text || '');
  }
  async function runHangulOnlyOCR(imageBase64, { singleLine = true } = {}, onStep = null) {
    if (!imageBase64) return '';
    const { data: { text } } = await Tesseract.recognize(
      imageBase64,
      'kor',
      { logger: () => { }, psm: singleLine ? 7 : 6 }
    );
    const hangulOnly = (text || '').match(KEEP_HANGUL)?.join('') ?? '';
    if (onStep) onStep();
    return hangulOnly.trim();
  }
  async function ocrDigitsRobust(img, onStep) {
    if (!img) { if (onStep) onStep(); return ''; }
    const tries = [7, 13, 6, 8, 10];
    let stepped = false;

    for (const psm of tries) {
      const t = (await runOCR(img, {
        lang: 'eng',
        whitelist: '0123456789.,',
        psm,
        classify_bln_numeric_mode: 1
      })).trim();

      const raw = t.replace(/[^\d.,]/g, '');
      const normalized = (() => {
        const parts = raw.split('.');
        if (parts.length <= 2) return raw;
        const last = parts.pop();
        return parts.join('') + '.' + last;
      })();
      const digits = normalized.replace(/[^0-9.]/g, '');

      if (digits.length > 0) {
        if (onStep && !stepped) { onStep(); stepped = true; }
        return digits;
      }
    }

    if (onStep && !stepped) onStep();
    return '';
  }

  /* ---------- 화면 공유 시작 ---------- */
  async function startCapture() {
    try {
      setBattleMainData({});
      setProfileInfo({ job: '', jobEngraving: '', level: '', power: '' });
      setComparison(null);
      setAverages({ main: [], add: [] });
      setAvgSnapshot(null);
      setCompareSnapshot(null);
      setError(null);
      setBadgeIds?.([]);

      setLoading(true);
      setViewMode('live');
      setStep('profile');

      const supports = navigator.mediaDevices.getSupportedConstraints?.() || {};
      const videoConstraints = { width: 1922, height: 1113 };
      if (supports.cursor) videoConstraints.cursor = 'never';

      const captureStream = await navigator.mediaDevices.getDisplayMedia({
        video: videoConstraints,
        audio: false,
      });

      setStream(captureStream);

      if (videoRef.current) {
        videoRef.current.srcObject = captureStream;
        await new Promise(resolve => {
          const v = videoRef.current;
          const ready = () => resolve();
          if (v.readyState >= 2 && v.videoWidth) resolve();
          else v.onloadedmetadata = ready;
        });
        await videoRef.current.play();
      }
    } catch (e) {
      setError('화면 공유 실패');
      setStep('idle');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  /* ---------- 프로필 OCR 파이프라인 ---------- */
  async function processProfileStep() {
    setError(null);
    setLoading(true);

    const isMine = await verifyMyCharMarker().catch(() => false);
    if (!isMine) {
      setLoading(false);
      setError('본인 캐릭터로 진행해 주세요.');
      return;
    }

    const plan = 4;
    setStepPlan('profile', plan);
    try {
      const jobTxt = await runHangulOnlyOCR(
        captureROIBase64(ROI.job, false),
        {},
        () => bumpStepProgress('profile')
      );
      const rawJob = (jobTxt || '').replace(/\s+/g, '');
      const { best: bestJob, score: jobScore } = findBestMatchWithScore(rawJob, KNOWN_JOBS);
      if (jobScore < 0.4) {
        setError('인식 실패 (직업)');
        setLoading(false);
        return;
      }
      const job = bestJob;

      const lvlDigits = await ocrDigitsRobust(
        captureROIBase64(ROI.level, true),
        () => bumpStepProgress('profile')
      );
      const pwrDigits = await ocrDigitsRobust(
        captureROIBase64(ROI.power, true),
        () => bumpStepProgress('profile')
      );

      const jeTxtRaw = await runHangulOnlyOCR(
        captureJobEngravingROI(),
        {},
        () => bumpStepProgress('profile')
      );
      const candidates = (JOB_CONFIG[job]?.engravings || []).map(e => e.name);
      const allEngs = candidates.length
        ? candidates
        : Object.values(JOB_CONFIG)
          .filter(v => v && Array.isArray(v.engravings))
          .flatMap(v => v.engravings.map(e => e.name));
      const { best: bestEng, score: jobEngScore } = bestEngravingMatch(jeTxtRaw, allEngs);
      if (jobEngScore < 0.4) {
        setError('인식 실패 (직업 각인)');
        setLoading(false);
        return;
      }
      const jobEngraving = bestEng;

      const level = normalizeFixed4_2(lvlDigits);
      const power = normalizeFixed4_2(pwrDigits);

      setProfileInfo({ job, jobEngraving, level, power });
      setStep('battleMain');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 메인 화면 OCR (1차) ---------- */
  async function processBattleMain() {
    setError(null);
    const activeJob = (profileInfo.job || '알수없음');
    const activeEngraving = (battleMainData.jobEngraving || profileInfo.jobEngraving || '');
    const { mainCount, addCount, traitNames } = getJobConfig(activeJob, activeEngraving);

    let availableMain = 0;
    for (let i = 1; i <= mainCount; i++) {
      if (ROI[`mainTrait${i}`] && ROI[`mainTrait${i}Value`]) availableMain++; else break;
    }
    const FIRST_PAGE_MAX = 14;
    const firstPageEnd = Math.min(FIRST_PAGE_MAX, addCount);
    let availableAddFirst = 0;
    for (let i = 1; i <= firstPageEnd; i++) {
      if (ROI[`addTrait${i}`] && ROI[`addTrait${i}Value`]) availableAddFirst++; else break;
    }
    const plan = 3 + (availableMain * 2) + (availableAddFirst * 2);
    setStepPlan('battleMain', plan);

    try {
      const rawRaidName = (await runOCR(
        captureBattleROI(ROI.raidName, 'meta'),
        { lang: 'kor+eng', psm: 7 },
        () => bumpStepProgress('battleMain')
      )).trim();
      console.log(rawRaidName)
      const normRaw = normalizeRaidNameForMatch(rawRaidName);
      const { stage: rawStage, gate: rawGate } = extractStageAndGate(normRaw);
      const rawCompact = compact(normRaw);

      let bestName = rawRaidName;
      let bestScore = -Infinity;
      let bestBaseSim = 0;
      for (const cand of RAID_NAMES) {
        const normCand = normalizeRaidNameForMatch(cand);
        const candCompact = compact(normCand);
        const { stage: candStage, gate: candGate } = extractStageAndGate(normCand);
        const baseSim = similarity(rawCompact, candCompact);
        let score = baseSim;
        if (rawGate && candGate && rawGate === candGate) score += 0.20;
        if (rawStage && candStage && rawStage === candStage) score += 0.10;
        if (score > bestScore) {
          bestScore = score;
          bestBaseSim = baseSim;
          bestName = cand;
        }
      }
      const raidName = bestName;
      if (bestBaseSim < 0.45) {
        setError('인식 실패 (레이드명)');
        setLoading(false);
        return;
      }

      const rawBattleTime = (await runOCR(captureBattleROI(ROI.battleTime, 'meta'), { lang: 'eng', whitelist: '0123456789:' }, () => bumpStepProgress('battleMain'))).trim();
      const battleTimeTxt = (() => {
        const parts = (rawBattleTime || '').split(':').map(p => parseInt(p, 10) || 0);
        let totalSec = 0;
        if (parts.length === 3) totalSec = parts[0] * 3600 + parts[1] * 60 + parts[2];
        else if (parts.length === 2) totalSec = parts[0] * 60 + parts[1];
        else totalSec = parts[0] || 0;
        const m = String(Math.floor(totalSec / 60)).padStart(2, '0');
        const s = String(totalSec % 60).padStart(2, '0');
        return `${m}:${s}`;
      })();

      const rawTimestamp = (await runOCR(captureBattleROI(ROI.timestamp, 'meta'), { lang: 'eng', whitelist: '0123456789.:-/ ' }, () => bumpStepProgress('battleMain'))).trim();
      const timestamp = normalizeTimestamp(rawTimestamp);

      const mainNameRaws = [];
      const mainValueRois = [];
      for (let i = 1; i <= availableMain; i++) {
        const nameROI = ROI[`mainTrait${i}`];
        const valueROI = ROI[`mainTrait${i}Value`];
        const nameRaw = (await runOCR(captureBattleROI(nameROI, 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain'))).trim();
        mainNameRaws.push(nameRaw);
        mainValueRois.push(valueROI);
      }

      const mainMatches = uniqueMatchWithScores(mainNameRaws, traitNames);
      const mainTraits = [];
      for (let idx = 0; idx < mainNameRaws.length; idx++) {
        const match = mainMatches[idx];
        const nameRaw = mainNameRaws[idx];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (메인 특성${idx + 1}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        const valueRaw = (await runOCR(
          captureBattleROI(mainValueRois[idx], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain')
        )).trim();
        const formatted = formatValueWithUnit(valueRaw, traitName);
        mainTraits.push({ trait: traitName, value: formatted });
      }

      const addNameRaws = [];
      const addValueRois = [];
      const addRowIndex = [];
      let consecutiveSkips = 0;
      for (let i = 1; i <= firstPageEnd; i++) {
        const raw = (await runOCR(captureBattleROI(ROI[`addTrait${i}`], 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain'))).trim();
        if (!raw) {
          consecutiveSkips++;
          if (consecutiveSkips >= 2) break;
          continue;
        }
        consecutiveSkips = 0;
        addNameRaws.push(raw);
        addValueRois.push(ROI[`addTrait${i}Value`]);
        addRowIndex.push(i);
      }
      const addMatches = uniqueMatchWithScores(addNameRaws, traitNames);
      const additionalTraits_first = [];
      for (let k = 0; k < addNameRaws.length; k++) {
        const match = addMatches[k];
        const nameRaw = addNameRaws[k];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (추가 특성${addRowIndex[k]}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        const valueRaw = (await runOCR(
          captureBattleROI(addValueRois[k], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain')
        )).trim();

        // const jeImg = captureBattleROI(addValueRois[0], 'value');
        // const imgEl = document.createElement('img');
        // imgEl.src = jeImg;
        // imgEl.style.position = 'fixed';
        // imgEl.style.top = '10px';
        // imgEl.style.right = '10px';
        // imgEl.style.border = '2px solid red';
        // imgEl.style.zIndex = 9999;
        // imgEl.style.maxWidth = '200px';
        // document.body.appendChild(imgEl);

        const formatted = formatValueWithUnit(valueRaw, traitName);
        additionalTraits_first.push({ trait: traitName, value: formatted });
      }

      enforceDamageRateUnits(mainTraits, additionalTraits_first);

      if (addCount > FIRST_PAGE_MAX) {
        partialRef.current = { raidName, battleTime: battleTimeTxt, timestamp, mainTraits, additionalTraits: additionalTraits_first };
        setStep('battleMain2');
        setLoading(false);
        return;
      }

      setBattleMainData({ raidName, battleTime: battleTimeTxt, timestamp, mainTraits, additionalTraits: additionalTraits_first });
      setStep('done');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 메인 화면 OCR (2차: 15~) ---------- */
  async function processBattleMain2() {
    setError(null);
    const job = profileInfo.job || '알수없음';
    const { addCount, traitNames } = getJobConfig(job, profileInfo.jobEngraving || '');
    const startIdx = 15;
    const endIdx = Math.min(addCount, 18);
    const map = (addCount <= 16) ? ROI_SECOND_PASS_16 : ROI_SECOND_PASS_18;

    const plan = (endIdx - startIdx + 1) * 2;
    setStepPlan('battleMain2', plan);

    try {
      const base = partialRef.current || {};
      const existedNames = new Set([
        ...((base.mainTraits || []).map(t => t.trait)),
        ...((base.additionalTraits || []).map(t => t.trait)),
      ]);

      const addNameRaws2 = [];
      const addValueRois2 = [];
      const addRowIndex2 = [];
      for (let i = startIdx; i <= endIdx; i++) {
        const nameROI = map[`addTrait${i}`];
        const valueROI = map[`addTrait${i}Value`];
        const raw = (await runOCR(captureBattleROI(nameROI, 'name'), { lang: 'kor' }, () => bumpStepProgress('battleMain2'))).trim();
        addNameRaws2.push(raw);
        addValueRois2.push(valueROI);
        addRowIndex2.push(i);
      }

      const addMatches2 = uniqueMatchWithScores(addNameRaws2, traitNames);
      const additionalTraits_second = [];
      for (let k = 0; k < addNameRaws2.length; k++) {
        const match = addMatches2[k];
        const nameRaw = addNameRaws2[k];
        if (!match || match.score < 0.4) {
          setError(`인식 실패 (추가 특성${addRowIndex2[k]}: ${nameRaw})`);
          setLoading(false);
          return;
        }
        const traitName = match.best;
        if (existedNames.has(traitName)) {
          setError('전투 분석기 스크롤 후 진행해 주세요');
          setLoading(false);
          return;
        }
        const valueRaw = (await runOCR(
          captureBattleROI(addValueRois2[k], 'value'),
          { lang: 'kor+eng', whitelist: '0123456789.,%만억조회', psm: 7 },
          () => bumpStepProgress('battleMain2')
        )).trim();
        const formatted = formatValueWithUnit(valueRaw, traitName);
        additionalTraits_second.push({ trait: traitName, value: formatted });
      }

      const mergedAdd = [].concat(base.additionalTraits || [], additionalTraits_second);
      enforceDamageRateUnits(base.mainTraits || [], mergedAdd);

      setBattleMainData({
        raidName: base.raidName || '',
        battleTime: base.battleTime || '',
        timestamp: base.timestamp || '',
        mainTraits: base.mainTraits || [],
        additionalTraits: mergedAdd,
      });
      partialRef.current = null;
      setStep('done');
      setLoading(false);
    } catch (e) {
      setError(e?.message || String(e));
      setLoading(false);
    }
  }

  /* ---------- 내 캐릭터 마커 검증 ---------- */
  async function verifyMyCharMarker() {
    const candidates = [
      { roi: MYCHAR_ROI, target: '1' },
      { roi: MYCHAR_ROI2, target: '2' },
    ];

    for (const { roi, target } of candidates) {
      const img = captureROIBase64(roi, false);
      if (!img) continue;

      const d1 = await ocrDigitsRobust(img);
      if (String(d1 || '').trim() === target) return true;

      const raw = (await runOCR(img, {
        lang: 'eng',
        whitelist: '012Il|! ',
        psm: 7,
      })).trim();

      const normalized = raw.replace(/[|!]/g, '1');
      const fixed = fixDigitLookalikes(normalized);
      const digits = fixed.replace(/\D/g, '');
      if (digits === target) return true;
    }
    return false;
  }

  /* ---------- 저장/불러오기/클라우드 동기화 ---------- */
  function saveRaid() {
    if (!battleMainData.raidName) return;
    const exists = savedRaids.some(r =>
      r.raidName === battleMainData.raidName &&
      r.timestamp === battleMainData.timestamp &&
      r.battleTime === battleMainData.battleTime
    );
    if (exists) { alert('이미 저장된 항목입니다'); return; }
    const newRecord = {
      id: Date.now().toString(),
      raidName: battleMainData.raidName,
      timestamp: battleMainData.timestamp,
      battleTime: battleMainData.battleTime,
      mainTraits: battleMainData.mainTraits || [],
      additionalTraits: battleMainData.additionalTraits || [],
      job: profileInfo.job || '',
      jobEngraving: profileInfo.jobEngraving || '',
      level: profileInfo.level || '',
      power: profileInfo.power || '',
      memo: memo.trim(),
      createdAt: new Date().toISOString(),
      source: 'local',
      _local: true,
      _cloud: false,
    };
    setSavedRaids(prev => [...prev, newRecord]);
    setMemo('');
    if (authUser?.uid) {
      const cloudPayload = toCloudDoc(newRecord);
      saveRaidCloud(authUser.uid, cloudPayload).catch(e => console.error('[Cloud Save Error]', e));
    }
  }
  const handleSelect = (vOrEvent) => {
    if (Array.isArray(vOrEvent)) {
      setSelectedIds(vOrEvent);
    } else {
      const values = Array.from(vOrEvent?.target?.selectedOptions ?? []).map(o => o.value);
      setSelectedIds(values);
    }
  };
  function loadRaid() {
    if (selectedIds.length < 1) return;
    const r = savedRaids.find(r => r.id === selectedIds[0]);
    if (r) {
      setBattleMainData({
        raidName: r.raidName,
        timestamp: r.timestamp,
        battleTime: r.battleTime,
        mainTraits: r.mainTraits,
        additionalTraits: r.additionalTraits,
        job: r.job || '',
        jobEngraving: r.jobEngraving || '',
        memo: r.memo || '',
      });
      setProfileInfo({
        job: r.job || '',
        jobEngraving: r.jobEngraving || '',
        level: r.level || '',
        power: r.power || '',
      });
      setComparison(null);
      setStep('done');

      try {
        if (stream) {
          stream.getTracks().forEach(t => t.stop());
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }
        const c = overlayRef.current;
        if (c) {
          const ctx = c.getContext('2d');
          if (ctx) ctx.clearRect(0, 0, c.width, c.height);
        }
      } catch (e) {
        console.warn('[Screen stop error]', e);
      }
      setStream(null);
      setLoadedId(r.id);
      setViewMode('live');
      setLoadedSelectionStamp(selectionStamp);
      setBadgeIds([r.id]);
    }
  }
  function handleReProfile() {
    setProgressByStep(prev => ({
      ...prev,
      profile: { done: 0, total: 0 },
      battleMain: { done: 0, total: 0 },
      battleMain2: { done: 0, total: 0 },
    }));
    setProfileInfo({ job: '', jobEngraving: '', level: '', power: '' });
    setBattleMainData({});
    setComparison(null);
    setStep('profile');
  }
  function rerunBattle() {
    setProgressByStep(prev => ({
      ...prev,
      battleMain: { done: 0, total: 0 },
      battleMain2: { done: 0, total: 0 },
    }));
    setStep('battleMain');
    setBattleMainData({});
    setComparison(null);
  }
  async function deleteFromCloudOnly() {
    if (!authUser?.uid) { alert('클라우드 삭제는 Google 로그인 후 가능합니다.'); return; }
    const ids = [...selectedIds];
    if (ids.length < 1) return;

    // 선택 항목 원본 확보 (raidName/timestamp/battleTime 사용)
    const selectedRecs = getSelectedRecords(ids, savedRaids);

    try {
      // 1) raids 스냅샷 삭제
      await Promise.all(ids.map(id => deleteRaidCloud(authUser.uid, id)));

      // 2) 연결된 analyses 로그 삭제 (동일 composite key 기준)
      await Promise.all(
        selectedRecs.map(r =>
          deleteUserAnalysesByKey(authUser.uid, {
            raidName:   r.raidName   ?? r?.payload?.raidName   ?? '',
            timestamp:  r.timestamp  ?? r?.payload?.timestamp  ?? '',
            battleTime: r.battleTime ?? r?.payload?.battleTime ?? '',
          })
        )
      );

      // 3) 파티 배지 캐시에서도 제거
      try {
        const storageKey = savedKeyStorageKey(authUser.uid);
        const raw = sessionStorage.getItem(storageKey);
        const arr = raw ? JSON.parse(raw) : [];
        const toRemove = new Set(
          selectedRecs
            .map(rec => makeCompositeKeyForPartyBadge(rec))
            .filter(Boolean)
        );
        const next = Array.isArray(arr) ? arr.filter(k => !toRemove.has(k)) : [];
        sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {}

      // 4) UI 동기화
      await refreshCloudMerge();
      setSelectedIds([]);
      window.dispatchEvent(new Event('ba:cloudDeleted'));
      alert(`클라우드에서 ${ids.length}건 삭제되었습니다. (전투 지표에는 반영되지 않습니다)`);
    } catch (e) {
      console.error('[Cloud Delete Error]', e);
      alert('클라우드 삭제 실패: ' + (e?.message || e));
    }
  }
  async function deleteRaid() {
    if (selectedIds.length < 1) return;
    const idSet = new Set(selectedIds);
    setSavedRaids(prev =>
      prev
        .map(r => {
          if (!idSet.has(r.id)) return r;
          if (r._cloud || r.source === 'cloud') {
            return { ...r, _local: false, source: 'cloud' };
          }
          return null;
        })
        .filter(Boolean)
    );
    setSelectedIds([]);
    setComparison(null);
    try { await refreshCloudMerge?.(); } catch { }
  }
  function saveSelectedToLocal() {
    if (selectedIds.length < 1) return;
    const idSet = new Set(selectedIds);
    setSavedRaids(prev =>
      prev.map(r =>
        idSet.has(r.id)
          ? { ...r, _local: true, source: r._cloud ? 'cloud' : 'local' }
          : r
      )
    );
  }
  async function uploadSelectedToCloud() {
    if (!authUser?.uid) { alert('로그인이 필요합니다.'); return; }
    const selectedRecords = selectedIds.map(id => savedRaids.find(r => r.id === id)).filter(Boolean);
    const targets = selectedRecords.filter(r => r.source !== 'cloud');
    if (targets.length === 0) { alert('클라우드에 저장할 로컬 전용 항목이 없습니다.'); return; }
    try {
      await Promise.all(
        targets.map(r => saveRaidCloud(authUser.uid, toCloudDoc(r)))
      );
      await refreshCloudMerge();
      alert(`${targets.length}건을 클라우드에 저장했습니다.`);
    } catch (e) {
      console.error('[Upload Selected Error]', e);
      alert('클라우드 저장 실패: ' + (e?.message || e));
    }
  }

  /* ---------- 비교/평균 계산 ---------- */
  function compareRaids() {
    if (selectedIds.length !== 2) return;
    const [r1, r2] = selectedIds.map(id => savedRaids.find(r => r.id === id)).filter(Boolean);
    if (!r1 || !r2) return;
    if (r1.job !== r2.job) { alert('같은 직업끼리만 비교할 수 있습니다.'); return; }
    if (r1.raidName !== r2.raidName) { alert('같은 레이드 항목만 비교할 수 있습니다.'); return; }
    const timeDiffSec = parseTime(r2.battleTime) - parseTime(r1.battleTime);
    const battleTimeDiff = formatDiffTime(timeDiffSec);
    const m1 = traitsToMap(r1.mainTraits, r1.additionalTraits);
    const m2 = traitsToMap(r2.mainTraits, r2.additionalTraits);
    const commonTraits = [...m1.keys()].filter(k => m2.has(k));
    const diffsRaw = commonTraits.map(trait => {
      const a = m1.get(trait), b = m2.get(trait);
      const dRaw = (b?.raw || 0) - (a?.raw || 0);
      if (!dRaw) return null;
      const isCount = trait.includes('횟수');
      const isRate = trait.includes('율') || trait.includes('률');
      let absStr;
      if (isCount) {
        absStr = Math.abs(dRaw).toFixed(0) + '회';
      } else if (isRate) {
        absStr = Math.abs(dRaw).toFixed(2) + '%';
        absStr = absStr.replace(/\.00%$/, '%');
      } else {
        const absNum = Math.abs(dRaw);
        if (absNum >= 1e12) absStr = (absNum / 1e12).toFixed(2) + '조';
        else if (absNum >= 1e8) absStr = (absNum / 1e8).toFixed(2) + '억';
        else if (absNum >= 1e4) absStr = (absNum / 1e4).toFixed(2) + '만';
        else absStr = absNum.toFixed(2);
        absStr = absStr.replace(/\.00(조|억|만)?$/, '$1');
      }
      const sign = dRaw >= 0 ? '↑' : '↓';
      return { trait, v1: a?.value || '', v2: b?.value || '', diff: `${sign}${absStr}` };
    }).filter(Boolean);

    const mainSet = new Set([
      ...(r1.mainTraits || []).map(t => t.trait),
      ...(r2.mainTraits || []).map(t => t.trait),
    ]);
    const addSet = new Set([
      ...(r1.additionalTraits || []).map(t => t.trait),
      ...(r2.additionalTraits || []).map(t => t.trait),
    ]);
    const diffsMain = [];
    const diffsAdd = [];
    for (const d of diffsRaw) {
      if (mainSet.has(d.trait)) diffsMain.push(d);
      else if (addSet.has(d.trait)) diffsAdd.push(d);
      else diffsAdd.push(d);
    }
    const ORDER_LOCAL = getJobConfig(r1.job || '알수없음', r1.jobEngraving || '').traitNames;
    const sortedMain = sortByKnownOrder(diffsMain, ORDER_LOCAL);
    const sortedAdd = sortByKnownOrder(diffsAdd, ORDER_LOCAL);

    setBattleMainData(prev => ({
      ...prev,
      job: r1.job,
      jobEngraving: r1.jobEngraving || '',
      raidName: r1.raidName,
    }));
    setComparison({
      raidName: r1.raidName,
      battleTimeDiff,
      mainDiffs: sortedMain,
      addDiffs: sortedAdd
    });
    setCompareSnapshot({ ids: [r1.id, r2.id], r1, r2 });
    setViewMode('compare');
    setBadgeIds([r1.id, r2.id]);
  }

  useEffect(() => {
    let raf;
    const tick = () => { drawOverlay(); raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [drawOverlay]);

  useEffect(() => {
    const onResize = () => drawOverlay();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [drawOverlay]);

  const jobForOrder = battleMainData.job || profileInfo.job || '알수없음';
  const engrForOrder = battleMainData.jobEngraving || profileInfo.jobEngraving || '';
  const ORDER = getJobConfig(jobForOrder, engrForOrder).traitNames;
  const mainView = sortByKnownOrder(battleMainData.mainTraits || [], ORDER);
  const addView = sortByKnownOrder(battleMainData.additionalTraits || [], ORDER);

  const compareMainItems = (comparison?.mainDiffs || []).map(d => ({
    trait: d.trait,
    value: `${d.v1} → ${d.v2} (${d.diff})`,
  }));
  const compareAddItems = (comparison?.addDiffs || []).map(d => ({
    trait: d.trait,
    value: `${d.v1} → ${d.v2} (${d.diff})`,
  }));

  const selectedRecords = selectedIds.map(id => savedRaids.find(r => r.id === id)).filter(Boolean);
  const sameJob = selectedRecords.length === 2 && (selectedRecords[0].job || '') === (selectedRecords[1].job || '');
  const sameRaid = selectedRecords.length === 2 && (selectedRecords[0].raidName || '') === (selectedRecords[1].raidName || '');
  const canCompare = sameJob && sameRaid;
  const selectedRecordsList = selectedIds.map(id => savedRaids.find(r => r.id === id)).filter(Boolean);
  const canUploadSelected = authUser?.uid && selectedRecordsList.length > 0 && selectedRecordsList.every(r => r.source !== 'cloud');
  const canAverage =
    selectedRecordsList.length > 1 &&
    selectedRecordsList.every(r => r.job === selectedRecordsList[0].job) &&
    selectedRecordsList.every(r => r.raidName === selectedRecordsList[0].raidName);

  function averageSelected() {
    const recs = selectedRecordsList;
    if (recs.length < 1) { alert('평균을 내려면 1개 이상 선택하세요.'); return; }
    const sameJob = recs.every(r => r.job === recs[0].job);
    const sameRaid = recs.every(r => r.raidName === recs[0].raidName);
    if (!sameJob || !sameRaid) { alert('같은 직업·같은 레이드의 기록만 평균을 낼 수 있습니다.'); return; }

    const allAvg = computeTraitAveragesFromRecords(recs);
    const byNorm = new Map(allAvg.map(t => [normalizeTraitName(t.trait), t.value]));

    const jobRef = (battleMainData.job || profileInfo.job || recs[0].job || '알수없음');
    const engrRef = (battleMainData.jobEngraving || profileInfo.jobEngraving || recs[0].jobEngraving || '');
    const ORDER_LOCAL = getJobConfig(jobRef, engrRef).traitNames;

    const mainNamesRef = new Set(recs.flatMap(r => (r.mainTraits || []).map(t => t.trait)));
    const addNamesRef = new Set(recs.flatMap(r => (r.additionalTraits || []).map(t => t.trait)));
    for (const n of mainNamesRef) addNamesRef.delete(n);

    const orderIndex = name => {
      const i = ORDER_LOCAL.indexOf(name);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    const sortByOrder = (a, b) => (orderIndex(a) - orderIndex(b)) || a.localeCompare(b);
    const mainNamesOrdered = Array.from(mainNamesRef).sort(sortByOrder);
    const addNamesOrdered = Array.from(addNamesRef).sort(sortByOrder);

    const avgMain = mainNamesOrdered.map(name => ({
      trait: name,
      value: byNorm.get(normalizeTraitName(name)) ?? '—'
    }));
    const avgAdd = addNamesOrdered.map(name => ({
      trait: name,
      value: byNorm.get(normalizeTraitName(name)) ?? '—'
    }));
    setAverages({ main: avgMain, add: avgAdd });

    const head = recs[0];
    setBattleMainData(prev => ({
      ...prev,
      job: head.job,
      jobEngraving: head.jobEngraving || '',
      raidName: head.raidName,
    }));

    setAvgSnapshot({
      ids: selectedRecordsList.map(r => r.id),
      levels: recs.map(r => r.level),
      powers: recs.map(r => r.power),
      times: recs.map(r => r.battleTime),
    });
    setViewMode('avg');
    setBadgeIds(selectedRecordsList.map(r => r.id));
  }

  const handleClickView = () => {
    if (viewMode !== 'live') {
      setViewMode('live');
      setComparison(null);
      setAverages({ main: [], add: [] });
      setAvgSnapshot(null);
      setCompareSnapshot(null);
      setBadgeIds(prev => (loadedId ? [loadedId] : prev));
    }
  };
  const handleClickCompare = () => {
    if (!canCompare) {
      alert('같은 직업·같은 레이드의 기록 2개를 선택하세요.');
      return;
    }
    compareRaids();
  };
  const handleClickAverage = () => {
    if (!canAverage) {
      alert('같은 직업·같은 레이드의 기록을 2개 이상 선택하세요.');
      return;
    }
    averageSelected();
  };

  /* ---------- 헤더 표시값 계산 ---------- */
  const fixedJob = (battleMainData.job || profileInfo.job || '');
  const fixedEngraving = (battleMainData.jobEngraving || profileInfo.jobEngraving || '');
  const fixedRaidName = (battleMainData.raidName || '');

  let displayLevel = profileInfo.level || '';
  let displayPower = profileInfo.power || '';
  let displayBattleTime = (viewMode === 'live') ? (battleMainData.battleTime || '') : '';

  if (viewMode === 'compare' && compareSnapshot?.r1 && compareSnapshot?.r2) {
    const { r1, r2 } = compareSnapshot;
    displayLevel = formatCompareNumber(r1.level, r2.level);
    displayPower = formatCompareNumber(r1.power, r2.power);
    displayBattleTime = formatCompareTime(r1.battleTime, r2.battleTime);
  }
  if (viewMode === 'avg' && avgSnapshot) {
    displayLevel = trim2(averageNumbers(avgSnapshot.levels || []));
    displayPower = trim2(averageNumbers(avgSnapshot.powers || []));
    displayBattleTime = averageTimesMMSS(avgSnapshot.times || []);
  }

  const liveDataReady = (
    viewMode === 'live' &&
    step === 'done' &&
    Boolean(
      (battleMainData?.raidName && battleMainData?.battleTime) ||
      (battleMainData?.mainTraits?.length > 0) ||
      (battleMainData?.additionalTraits?.length > 0)
    )
  );

  const hasOCRProfile =
    Boolean(profileInfo.job || profileInfo.jobEngraving || profileInfo.level || profileInfo.power);

  const hasBattleData = liveDataReady;

  const headerJob = suppressDisplay ? '' : (battleMainData.job || profileInfo.job || fixedJob || '');
  const headerEngraving = suppressDisplay ? '' : (battleMainData.jobEngraving || profileInfo.jobEngraving || fixedEngraving || '');
  const headerLevel = suppressDisplay ? '' : (liveDataReady ? (profileInfo.level || '') : (displayLevel || profileInfo.level || ''));
  const headerPower = suppressDisplay ? '' : (liveDataReady ? (profileInfo.power || '') : (displayPower || profileInfo.power || ''));
  const headerRaidName = suppressDisplay ? '' : (battleMainData.raidName || fixedRaidName || '');
  const headerTimestamp = suppressDisplay ? '' : (battleMainData.timestamp || '');
  const headerBattleTime = suppressDisplay ? '' : (liveDataReady ? (battleMainData.battleTime || '') : (displayBattleTime || ''));

  const viewROI = ROI_PRESETS[step] || ROI_PRESETS.idle;

  /* ---------- 렌더 ---------- */
  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">
      {!ready ? null : user ? (
        <h1 className="text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white">
          {user.displayName}님의 전투 분석 기록
        </h1>
      ) : (
        <h1 className="text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white">
          내 컴퓨터 로컬 기록
        </h1>
      )}

      <div className="hidden lg:block">
        <ControlsBar
          stream={stream}
          step={step}
          loading={loading}
          memo={memo}
          setMemo={setMemo}
          onStartCapture={startCapture}
          onRunProfile={processProfileStep}
          onRunMain={processBattleMain}
          onRunMain2={processBattleMain2}
          onReProfile={handleReProfile}
          onSaveRaid={saveRaid}
          onRerunBattle={rerunBattle}
        />
        <StepProgressBar
          step={step}
          getStepPercent={getStepPercent}
          loading={loading}
          error={error}
          showPage2Progress={showPage2Progress}
        />
      </div>

      <div className={`flex flex-col lg:flex-row items-stretch ${bigView ? 'gap-0' : 'gap-6'} mb-3 lg:mb-6 min-h-0`}>
        <div
          className={`${bigView ? 'w	full' : 'lg:basis-3/5 w-full'} bg-black rounded-lg overflow-hidden relative hidden lg:flex items-center justify-center`}
          style={{ aspectRatio: `${viewROI.w} / ${viewROI.h}` }}
        >
          <div className='absolute top-2 right-2 z-10 flex gap-2'>
            <button
              type="button"
              onClick={() => setBigView(v => !v)}
              className="rounded-md px-3 py-1 text-xs text-white bg-white/10 hover:bg-white/20 transition-colors"
              aria-pressed={bigView}
              title={bigView ? '작게보기' : '크게보기'}
            >
              {bigView ? '작게보기' : '크게보기'}
            </button>

            <button
              type="button"
              onClick={() => setShowOverlay(v => !v)}
              className="rounded-md px-3 py-1 text-xs text-white bg-white/10 hover:bg-white/20 transition-colors"
              aria-pressed={showOverlay}
              title={showOverlay ? '오버레이 숨기기' : '오버레이 표시'}
            >
              {showOverlay ? '오버레이 숨기기' : '오버레이 표시'}
            </button>
          </div>
          <ScreenCapture
            videoRef={videoRef}
            overlayRef={overlayRef}
            viewROI={viewROI}
          />
        </div>

        {!bigView && (
          <>
            <div className="hidden lg:flex lg:flex-col lg:min-h-0 lg:basis-2/5 relative">
              {(step !== 'idle' && step !== 'done') && (
                <div className="absolute inset-0 z-20 flex items-center justify-center
                                bg-white/60 dark:bg-zinc-900/50 backdrop-blur-[2px]
                                pointer-events-auto">
                  <div className="px-4 py-2 rounded-md text-sm font-medium
                                  bg-white/90 dark:bg-zinc-900/90
                                  text-zinc-800 dark:text-zinc-200 shadow">
                    화면 공유 중에는 기록 패널을 사용할 수 없습니다
                  </div>
                </div>
              )}
              <SavedRaidsPanel
                authUser={authUser}
                savedRaids={savedRaids}
                selectedIds={selectedIds}
                onSelect={handleSelect}
                canCompare={canCompare}
                onCompare={handleClickCompare}
                canAverage={canAverage}
                onAverage={handleClickAverage}
                onView={handleClickView}
                onCloudLoad={refreshCloudMerge}
                onUploadSelectedToCloud={uploadSelectedToCloud}
                onDeleteFromCloudOnly={deleteFromCloudOnly}
                onLoadRaid={loadRaid}
                onDeleteRaid={deleteRaid}
                canUploadSelected={canUploadSelected}
                onSaveSelectedToLocal={saveSelectedToLocal}
              />
            </div>
          </>
        )}
      </div>

      <MobileSavedRaidsFab
        onOpen={() => setMobileSavedOpen(true)}
        shown={!mobileSavedOpen && fabShown}
      />

      {loginHint && (
        <div className="pb-4 text-sm text-rose-500 dark:text-rose-400 lg:hidden whitespace-pre-line">
          {loginHint}
        </div>
      )}

      {viewMode !== 'live' && (
        <div className="mb-6 flex flex-wrap items-center gap-2">
          {viewMode === 'avg' ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-zinc-50 text-zinc-700 border border-zinc-200 px-2 py-1 text-xs dark:bg-zinc-400/20 dark:text-zinc-300 dark:border-zinc-300/20">
              평균
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 text-rose-700 border border-rose-200 px-2 py-1 text-xs dark:bg-rose-400/20 dark:text-rose-300 dark:border-rose-300/20">
              비교
            </span>
          )}
          {badgeIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {badgeIds.map(id => {
                const item = savedRaids.find(r => r.id === id);
                if (!item) return null;
                const label = [
                  item.job || '직업?',
                  item.jobEngraving ? `(${item.jobEngraving})` : '',
                  item.raidName ? ` / ${item.raidName}` : '',
                  item.timestamp ? ` (${formatYMD(item.timestamp)})` : '',
                ].join('');
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 text-xs dark:bg-indigo-400/20 dark:text-indigo-300 dark:border-indigo-300/20"
                    title={label}
                  >
                    <span className="max-w-[18rem] truncate">{label}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mb-6">
        {(viewMode === 'compare' || viewMode === 'avg' || hasOCRProfile) && (
          <ProfileInfoCard
            job={headerJob}
            engraving={headerEngraving}
            level={headerLevel}
            power={headerPower}
          />
        )}
        {(viewMode === 'compare' || viewMode === 'avg' || hasBattleData) && (
          <BattleMetaInfo
            raidName={headerRaidName}
            timestamp={headerTimestamp}
            battleTime={headerBattleTime}
            isCompare={viewMode === 'compare'}
            isAverage={viewMode === 'avg'}
          />
        )}
      </div>

      {viewMode === 'compare' ? (
        <>
          <TraitsList title="메인 특성" items={compareMainItems} />
          <TraitsList title="추가 특성" items={compareAddItems} />
        </>
      ) : viewMode === 'avg' ? (
        <>
          <TraitsList title="메인 특성" items={averages.main} />
          <TraitsList title="추가 특성" items={averages.add} />
        </>
      ) : (viewMode === 'live' && suppressDisplay) ? (
        <>
          <TraitsList title="메인 특성" items={[]} />
          <TraitsList title="추가 특성" items={[]} />
        </>
      ) : (
        <>
          <TraitsList title="메인 특성" items={liveDataReady ? (battleMainData.mainTraits || []) : mainView} />
          <TraitsList title="추가 특성" items={liveDataReady ? (battleMainData.additionalTraits || []) : addView} />
        </>
      )}

      <MobileBottomSheet open={mobileSavedOpen} onClose={() => setMobileSavedOpen(false)}>
        <div className="flex flex-1 min-h-0 flex-col relative">
          <SavedRaidsPanel
            authUser={authUser}
            savedRaids={savedRaids}
            selectedIds={selectedIds}
            onSelect={(v) => { handleSelect(v); }}
            canCompare={canCompare}
            onCompare={() => { handleClickCompare(); setMobileSavedOpen(false); }}
            canAverage={canAverage}
            onAverage={() => { handleClickAverage(); setMobileSavedOpen(false); }}
            onView={handleClickView}
            onCloudLoad={refreshCloudMerge}
            onUploadSelectedToCloud={uploadSelectedToCloud}
            onDeleteFromCloudOnly={deleteFromCloudOnly}
            onLoadRaid={() => { loadRaid(); setMobileSavedOpen(false); }}
            onDeleteRaid={deleteRaid}
            canUploadSelected={canUploadSelected}
            onSaveSelectedToLocal={saveSelectedToLocal}
          />
        </div>
      </MobileBottomSheet>
    </div>
  );
}
